import React__default, { Fragment, createElement, createContext, useState, useContext, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { isMobile, isIPad13 } from 'react-device-detect';
import Countdown, { zeroPad } from 'react-countdown';
import LazyLoad from 'react-lazyload';
import { Text } from '@it25syv/25syv-ui';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

var _templateObject, _templateObject2, _templateObject3, _templateObject4;
var CenterBlock = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    max-width: 1150px;\n    margin: 0 auto;\n    box-sizing: border-box;\n    padding: 0 15px;\n    position: relative;\n\n    @media (max-width: ", ") {\n        padding: 0 15px;\n    }\n"])), BREAKPOINTS.sm + 'px');
var MediaGrid = styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    display: grid;\n    grid-template-columns: repeat(2, 1fr);\n    gap: 0.75rem;\n    position: relative;\n\n    @media (min-width: ", ") {\n        grid-template-columns: repeat(3, 1fr);\n    }\n\n    @media (min-width: ", ") {\n        ", "\n    }\n\n    @media (min-width: ", ") {\n        ", "\n        gap: ", ";\n    }\n"])), BREAKPOINTS.sm + 'px', BREAKPOINTS.md + 'px', function (_ref) {
  var itemsPerRow = _ref.itemsPerRow;
  return "grid-template-columns: repeat(" + (itemsPerRow || 4) + ", 1fr);";
}, BREAKPOINTS.lg + 'px', function (_ref2) {
  var itemsPerRow = _ref2.itemsPerRow;
  return "grid-template-columns: repeat(" + (itemsPerRow || 4) + ", 1fr);";
}, function (_ref3) {
  var gap = _ref3.gap;
  return gap || '2rem 1rem';
});
var HighlightBox = styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n    font-size: 12px;\n    height: 20px;\n    background: rgba(0, 0, 0, 0.8);\n    border-radius: 5px;\n    padding: 0 5px;\n    color: #fff;\n    line-height: 20px;\n    display: inline-block;\n\n    @media (max-width: ", ") {\n        height: 17px;\n        line-height: 17px;\n        font-size: 11px;\n    }\n"])), BREAKPOINTS.md + 'px');
var TimerWrapper = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    width: 183px;\n    top: ", ";\n    left: ", ";\n    transform: translate(-50%, -50%);\n    z-index: 1;\n\n    @media (max-width: ", ") {\n        width: 119px;\n    }\n"])), function (props) {
  return props.top ? props.top : '50%';
}, function (props) {
  return props.left ? props.left : '50%';
}, BREAKPOINTS.lg + 'px');

var UI_CONFIG = {
  menuColors: {
    btnColor: '#333333',
    btnActiveColor: '#FDE9E9',
    btnActiveTextColor: '#DA1D1B',
    btnHoverColor: '#ebebeb'
  },
  textColor: '#646464',
  loaderColor: '#646464'
};

var _templateObject$1, _templateObject2$1;
var MENU_COLORS = UI_CONFIG.menuColors;
var StyledButton = styled.button(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteralLoose(["\n    height: 37px;\n    font-size: 16px;\n    border: 1px solid transparent;\n    border-radius: 5px;\n    text-transform: none;\n    cursor: pointer;\n    padding: 0 20px;\n    font-weight: bold;\n    margin: 0 11px 0 0;\n    background: ", ";\n    color: ", ";\n\n    :hover {\n        ", ";\n    }\n"])), function (props) {
  return props.active ? props.btnActiveColor : 'none';
}, function (props) {
  return props.active ? props.btnActiveTextColor : MENU_COLORS.btnColor;
}, function (props) {
  return !props.active && css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteralLoose(["\n                color: ", ";\n                background: ", ";\n            "])), MENU_COLORS.btnColor, MENU_COLORS.btnHoverColor);
});

var ButtonList = function ButtonList(_ref) {
  var _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function (f) {
    return f;
  } : _ref$onChange,
      _ref$buttonSet = _ref.buttonSet,
      buttonSet = _ref$buttonSet === void 0 ? [] : _ref$buttonSet,
      _ref$btnActiveColor = _ref.btnActiveColor,
      btnActiveColor = _ref$btnActiveColor === void 0 ? MENU_COLORS.btnActiveColor : _ref$btnActiveColor,
      _ref$btnActiveTextCol = _ref.btnActiveTextColor,
      btnActiveTextColor = _ref$btnActiveTextCol === void 0 ? MENU_COLORS.btnActiveTextColor : _ref$btnActiveTextCol;
  return /*#__PURE__*/React__default.createElement(Fragment, null, buttonSet.map(function (button, key) {
    return /*#__PURE__*/React__default.createElement(StyledButton, {
      key: key,
      onClick: function onClick() {
        return onChange(button);
      },
      active: value === button.url,
      btnActiveColor: btnActiveColor,
      btnActiveTextColor: btnActiveTextColor
    }, button.title ? button.title : button.label);
  }));
};

ButtonList.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  buttonSet: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    title: PropTypes.string
  })).isRequired,
  btnActiveColor: PropTypes.string,
  btnActiveTextColor: PropTypes.string
};

var _templateObject$2;
var ButtonListHolder = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteralLoose(["\n    display: flex;\n    overflow-x: scroll;\n    -ms-overflow-style: none;\n    scrollbar-width: none;\n    padding: 0 0 0 10px;\n\n    &::-webkit-scrollbar {\n        display: none;\n    }\n"])));

var ButtonsSwitcher = function ButtonsSwitcher(_ref) {
  var tabs = _ref.tabs,
      activeTab = _ref.activeTab,
      onChange = _ref.onChange,
      menuColors = _ref.menuColors;
  return /*#__PURE__*/React__default.createElement(ButtonListHolder, null, /*#__PURE__*/React__default.createElement(ButtonList, {
    buttonSet: tabs,
    value: activeTab,
    onChange: onChange,
    btnActiveColor: menuColors.btnActiveColor,
    btnActiveTextColor: menuColors.btnActiveTextColor
  }));
};

ButtonsSwitcher.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    title: PropTypes.string
  })).isRequired,
  menuColors: PropTypes.objectOf(PropTypes.string)
};

var detectMobile = function detectMobile() {
  return isMobile || isIPad13;
};
var detectIpad = function detectIpad() {
  return isIPad13;
};

var getSiteName = window.location.hostname.replace(/.+\/\/|www.|test.|dev.|\..+/g, '');
var getEvoSkin = function getEvoSkin(siteName) {
  if (siteName === void 0) {
    siteName = 'rod25';
  }

  var getSkin = function getSkin(skin) {
    return {
      skin: skin,
      brand: skin
    };
  };

  var skin = {
    rod25: getSkin(1),
    casinogo: getSkin(2),
    bet25: getSkin(3),
    derby25: getSkin(4)
  };
  return skin[siteName];
};
function getApiUrl() {
  return apiEndPointGenarator("https://api." + BASE_SITE_NAMES.dev + "/casino", "https://api." + BASE_SITE_NAMES.test + "/casino", "https://api." + BASE_SITE_NAMES.prod + "/casino");
}
function getBetApiUrl() {
  return apiEndPointGenarator("https://api." + BASE_SITE_NAMES.dev + "/util/", "https://api." + BASE_SITE_NAMES.test + "/util/", "https://api." + BASE_SITE_NAMES.prod + "/util/");
}
function ENV() {
  return apiEndPointGenarator('dev', 'test', 'prod');
}
function apiEndPointGenarator(dev, test, prod) {
  if (window.location.hostname.indexOf('dev') > -1) {
    return dev;
  } else {
    switch (window.location.hostname) {
      case 'localhost':
      case BASE_SITE_NAMES.dev:
        return dev;

      case BASE_SITE_NAMES.test:
        return test;

      default:
        return prod;
    }
  }
}

var APP_NAME = 'rod25';
var BASE_SITE_NAMES = {
  dev: 'bet25dev.dk',
  test: 'bet25test.dk',
  prod: 'bet25.dk'
};
var CMS_PATH = apiEndPointGenarator("https://cms." + BASE_SITE_NAMES.dev, "https://cms." + BASE_SITE_NAMES.test, "https://cms." + BASE_SITE_NAMES.prod);
var IMG_PATH = CMS_PATH + '/upload/files/rod25/images/uploads/roed25/img/live-casino/';
var getPlatformForApi = function getPlatformForApi() {
  return !detectMobile() || detectIpad() ? 'web' : 'mobile';
};
var API_URL = getApiUrl() + "/v2/";
var GAME_LAUNCH_API = getApiUrl() + "/games/";
var MAIN_REST_BASE_API = getBetApiUrl() + "rod25/api.php";
var CONTENT_API_BASE_URL = "" + getBetApiUrl();
var CDN_IMG_PATH = apiEndPointGenarator("https://cdn." + BASE_SITE_NAMES.dev + "/casino/img/thumbs/", "https://cdn." + BASE_SITE_NAMES.prod + "/casino/img/thumbs/", "https://cdn." + BASE_SITE_NAMES.prod + "/casino/img/thumbs/");

var _g;

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function SvgEvolutionIcon(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 8.216,
    height: 12
  }, props), _g || (_g = /*#__PURE__*/createElement("g", {
    "data-name": "evolution icon",
    fill: "#fff"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Path 6809",
    d: "M7.522 11.858a.553.553 0 11.392-.162.554.554 0 01-.392.162m0-1.25a.7.7 0 10.695.7.7.7 0 00-.695-.7"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Union 49",
    d: "M6.387 5.51C1.677 5.362.96 2.839.004.685a5.5 5.5 0 017.02 1.437A6.178 6.178 0 018.217 5.46q-.684.056-1.372.057-.229-.001-.458-.007z"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Union 50",
    d: "M0 10.708a16.5 16.5 0 011.155-2.367c.835-1.276 2.17-2.308 5-2.448a16.076 16.076 0 012.058.04 5.528 5.528 0 01-5.5 5.46A5.753 5.753 0 010 10.708z"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Path 7204",
    d: "M7.256 11.192h-.1v-.068h.295v.068h-.1v.294h-.088z"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Path 7205",
    d: "M7.484 11.122h.136l.068.232.068-.232h.136v.363h-.083v-.293l-.092.293h-.068l-.088-.292v.292h-.075v-.363z"
  }))));
}

var _g$1;

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

function SvgAuthenticIcon(props) {
  return /*#__PURE__*/createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 11.332,
    height: 12
  }, props), _g$1 || (_g$1 = /*#__PURE__*/createElement("g", {
    "data-name": "Authinctic icon",
    fill: "#fff"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Path 120",
    d: "M8.481 6.116H3.955a.973.973 0 00-.035.327 1.33 1.33 0 00.063.438h3.511a1.911 1.911 0 11.008-1.083h.958a2.821 2.821 0 00-.153-.528l-.247.1a2.6 2.6 0 00-1.064-1.241l.142-.237a2.826 2.826 0 00-1.046-.379l-.033.236a2.6 2.6 0 00-1.607.3l-.128-.233a2.857 2.857 0 00-.915.776l.215.167a2.6 2.6 0 00-.543 1.542h-.26v.029a2.835 2.835 0 00.216 1.09l.235-.1a2.6 2.6 0 001.064 1.241l-.125.209a2.826 2.826 0 001.07.374l.033-.239a2.6 2.6 0 001.607-.3l.118.212a2.859 2.859 0 00.864-.735l-.2-.152a2.6 2.6 0 00.543-1.542h.258V6.32a.781.781 0 00-.029-.212"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Path 121",
    d: "M6.945.171A.285.285 0 006.698 0H4.679a.286.286 0 00-.248.171L.013 11.828A.118.118 0 00.135 12h2.6a.449.449 0 00.3-.14s1.415-1.678 2.656-1.678 2.656 1.678 2.656 1.678a.449.449 0 00.3.14h2.555a.119.119 0 00.119-.171zM5.78 9.146a2.8 2.8 0 112.71-2.71 2.8 2.8 0 01-2.71 2.71"
  }))));
}

var _style, _path, _path2, _path3, _path4, _path5;

function _extends$3() {
  _extends$3 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$3.apply(this, arguments);
}

function SvgBaccarat(props) {
  return /*#__PURE__*/createElement("svg", _extends$3({
    id: "baccarat_svg__Lag_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: 0,
    y: 0,
    viewBox: "0 0 110 110",
    xmlSpace: "preserve"
  }, props), _style || (_style = /*#__PURE__*/createElement("style", null, ".baccarat_svg__st1{fill:#fff}")), _path || (_path = /*#__PURE__*/createElement("path", {
    fill: "none",
    d: "M0 0h110v110H0z"
  })), _path2 || (_path2 = /*#__PURE__*/createElement("path", {
    className: "baccarat_svg__st1",
    d: "M107 59.9v-7.3c0-2.1 0-4.2-2.1-5.5s-6-.7-8.4-.7h-8c-.2.7-.4 1.3-.7 2 0 .1 0 .1-.1.2h12.4c2 0 4.5-.3 4.6 2.4.2 4.4 0 9 0 13.4v29.2c0 1.1.3 2.8-.3 3.9-.8 1.3-2.3 1.1-3.6 1.1H39.7c-1.1 0-2.5.2-3.3-.5-1.4-1.2-.8-4.7-.8-6.3V75.3h-1.4c-.3 0-.6.1-.7.3V95.4c0 2.8 1.2 5.3 4.5 5.5H98.7c3.6 0 8.3.4 8.4-4.6.2-12.1-.1-24.3-.1-36.4z"
  })), _path3 || (_path3 = /*#__PURE__*/createElement("path", {
    className: "baccarat_svg__st1",
    d: "M34.5 75.3c2.4-.1 4.7-.5 7-1.1L56.2 70c4-1.1 8-2.2 12-3.4 9-2.6 16.3-9.3 19.6-18.1.2-.7.5-1.3.7-2 .8-2.9 1.2-5.9 1.1-8.9-.3-10.4-6-19.8-15-24.9-7.6-4.3-15.9-4.5-24.1-2.2l-16.3 4.6c-4.4 1.2-9 2.2-13.2 4C6.2 25.9-.4 43.3 6.3 58c2.1 4.6 5.3 8.6 9.3 11.6 5.3 3.9 11.9 5.9 18.5 5.7h.4zM10.3 60.8C4.6 52 4.5 40.7 10 31.7c3.9-6.3 9.9-10.2 16.9-12.2l26-7.3c14.4-4.1 29.5 4.2 33.6 18.7 2.5 8.8.5 18.2-5.4 25.1-4.2 5-9.7 7.6-15.8 9.3l-16.2 4.5c-4.3 1.2-8.7 2.8-13.1 3.3-10.2 1-20.1-3.7-25.7-12.3zM73.4 74.3l.2-3.5c0-.6 0-1.2.1-1.8.2-.2.8-.1 1-.1 1.2-.1 3.2.2 4-1-1.1-1.1-3.5-.5-4.9-.3.1-.5.1-1 .2-1.5-.3.2-.7.3-1 .4-.5 0-.7.3-.6.8s0 .6-.3.6l-.7.1c-1.2.2-2.4.3-3.7.4 1.1 1 3.3.9 4.6.7v3.7c0 .6.1 1.2-.3 1.5-.5.1-1.1.2-1.7.2-2.4.3-4.7.4-7.1.5.6 1.4 2.2 1.3 3.4 1.3 1.7-.1 3.4-.2 5-.3 3.7-.4 7.4-.4 11.1.1-1.1-3.3-6.8-1.9-9.3-1.8zM86.8 88v4.1c.3 0 1.4.2 1.6 0s0-1.1 0-1.3v-2.2c.1-.7-.2-.6.6-.6 1.4 0 2.1 2.2 2.6 3.3s1.1.8 2.4.8c-.7-1.5-1.4-3.3-2.8-4.3 1.5-.3 2.4-1.8 2.1-3.3 0-.1 0-.2-.1-.3-.4-1.6-1.7-1.9-3.1-1.9h-2.5c-.8 0-.7-.1-.8.3-.1.7-.1 1.5 0 2.2V88zm1.7-3.9c.2-.3 1.5-.2 1.9-.1 1.2.1 2 1.7.7 2.4-.8.2-1.7.2-2.6.1 0-.5-.3-2.1 0-2.4zM79.4 82.3c-.2.2 0 1.5 0 1.7v8H82c.8 0 2.4.3 3.2 0 .4-.2.2.1.3-.5.1-.4.1-.8 0-1.1-.4-.4-2.5 0-3.1 0-1 0-1.2.3-1.3-.6-.1-.5-.2-1.8.2-2 .6-.4 2.5.1 3.2 0 .1 0 .6.1.6 0 .1-.5.1-.9 0-1.4-.3-.5-1.3-.3-2-.3-.4 0-1.8.2-2 0-.2-.7-.2-1.5 0-2.2.4-.3 2.3 0 2.8 0 1.1 0 1.4.3 1.4-.8 0-1 0-.8-.9-.8H81c-.2 0-1.4-.2-1.6 0zM65.1 69l-1.7.5c-1.3 3.5-3.5 6.2-5.6 9.3 3.5-2.3 6.1-5.8 7.3-9.8zM52.2 86.9c1.1-.7 1.4-2.1.9-3.3-.5-1.1-1.5-1.3-2.6-1.3H47v7.2c-.1.7-.1 1.4 0 2.1.2.6-.1.4.7.4h2.6c1.4 0 2.6-.1 3.2-1.6.7-1.3.1-2.9-1.3-3.5.1 0 0 0 0 0zM48.7 84c.8-.1 1.7-.1 2.5.1 1.2.6.6 2-.5 2.2-.7.1-1.3.1-2 0-.3-.4 0-2 0-2.3zm1.9 6.5c-.5 0-1.7.2-2-.2s0-1.9 0-2.4c.9-.1 1.8-.1 2.7.1 1.6.7.7 2.3-.7 2.5zM59.4 83.2c-.3-.9-.2-.8-1.1-.8s-.8 0-1.1.8c-.4 1.2-.7 2.3-1.1 3.5l-1.8 5.5c.4 0 1.4.2 1.7 0s.3-.8.4-1.1.2-1 .4-1.1 1.3 0 1.6 0c1.4 0 1.5-.2 2 1.1.1.4.2.9.5 1.1s1.2 0 1.6 0c-.6-1.9-1.2-3.7-1.8-5.6l-1.3-3.4zm-2.2 5l1.1-3.6 1.1 3.6h-2.2zM69.6 83.6c0-.6.2-1.1-.2-1.3-.4-.1-.8-.1-1.3 0-.1.1 0 .6 0 .7-.1 1.9 0 3.9 0 5.8l-2.3-4.6c-.2-.4-.6-1.7-1-1.9-.5-.1-1.1-.1-1.6 0-.2.2 0 1.5 0 1.8V92c.3 0 1.3.1 1.5-.1s0-1.6 0-1.9v-4.3c.8 2 1.8 4 3 5.9.1.1.1.4.3.5.5.1 1 .1 1.5 0 .2-.2.1-1.1.1-1.4v-7.1zM75.5 86.4c-.2-1.2 2.1-3.1 2.8-4.1-.7-.1-1.4-.1-2.1 0-.3.1-.6.7-.7.9l-2.6 3.4v-2c0-.4.2-1.9 0-2.3-.5-.1-1.1-.2-1.6-.1-.1.1 0 .8 0 1V92c.3 0 1.4.2 1.6 0s0-1.6 0-1.9c0-1.3.5-1.7 1.3-2.7l1.8 3.7c.2.4.3.7.6.9.6.1 1.3.1 1.9 0-.4-.8-.9-1.6-1.3-2.4s-1.5-2.2-1.7-3.2z"
  })), _path4 || (_path4 = /*#__PURE__*/createElement("path", {
    className: "baccarat_svg__st1",
    d: "M41.4 37.6c-.8 1.5-1.4 3-2 4.5 1.4-.2 1.5-.7 2.3-2s1.7-2.7 2.5-4c.5 1.6 1 3.2 1.6 4.8.1.4.9 2.9 1.3 3 .8.1.1-2.5 0-3-.3-1.8-.8-3.5-1.2-5.3 1.5.8 3 1.5 4.5 2.2 1.2.6 1.5.8 2.7.4-1.3-1.1-2.7-2.1-4.2-2.9-.9-.5-1.8-1.1-2.7-1.6s-1-.5-1.2-1.4l1.1-.3c1.7-.4 3.4-1.1 5-1.9 1.3-.9-.4-.7-1.2-.6-1.3.2-2.7.5-4 .8-.3.1-.7.2-1 .2-.6 0-.5 0-.7-.6s-.6-1.6-1-2.4c-.5-1.1-.9-1-.8.2.1.9.3 1.7.4 2.6.2 1 0 .8-.9 1.1s-5.4 1.4-5.5 2.3 3 0 3.4 0c1.2-.2 2.4-.5 3.6-.8.2.7.1 1.4-.4 2-.7.8-1.2 1.7-1.6 2.7zM33.4 56.4c-1.2-.9-2.4-.4-3.7 0-.4.1-2.2.4-2.4.7s.4 1.5.5 1.8l1 3.6 1.4 4.8c.3-.1 1.4-.2 1.5-.4s0-.4-.1-.8c-.2-1-.6-2.1-.9-3.1 1.1-.3 2.6-.5 3.4-1.5 1.3-1.5.7-4.1-.7-5.1zm-1.9 4.5c-1 .3-1.1.5-1.4-.4l-.6-2.1c.8-.2 2-.8 2.8-.3 1.6.9.4 2.4-.8 2.8zM35.5 28.5c.2.1 2.7 1.1 1.9-.2-.8-1.2-1.9-2.3-3.2-3-.6-.3-2.2-1.2-1.9.2.3 1.2 2.2 2.4 3.2 3zM34.1 75.3h.4-.4zM42.3 63.8c.2-.1 1.5-.2 1.6-.5s-.4-1.4-.5-1.7l-1.7.5c-.7.3-1.5.5-2.2.6-.5-.1-.1.2-.4-.3-.3-.6-.4-1.3-.5-1.9-.5-1.7-1-3.4-1.4-5.1 0-.2-.1-.9-.3-1s-1.4.4-1.7.5L38.1 65l4.2-1.2z"
  })), _path5 || (_path5 = /*#__PURE__*/createElement("path", {
    className: "baccarat_svg__st1",
    d: "M39.5 26.1c1.6-.2 3.2-.6 4.8-1.1l3.1-.8c1-.4 2.1-.7 3.1-.8.7 0 .5 0 .8.8l.3 1.1c.1.3.2.6.3 1 .7 2.4 1.3 4.8 2 7.2.4 1.5.8 2.9 1.3 4.3.3 1 1.4 3.1.4 4s-3.3.7-4.6 1.6c-.2.2-.7.5-.1 1 .2.2.7 0 1-.1 2.6-.6 6.3-.8 5.6-4.4-1.1-4.9-2.6-9.8-4-14.7-.2-.7-.4-1.5-.6-2.2-.1-.4-.2-1.3-.5-1.6s-1.3.1-1.9.3c-3.7 1.1-7.4 2.1-11 3.4-.4.2-1.1.5-.4 1.1 0 .1.1-.1.4-.1zM47.1 52.5c-.6-.8-.4-.9-1.5-.6s-.9.3-.9 1.2l-.1 3.9c-.1 2.1-.1 4.1-.2 6.2.4-.1 1.5-.2 1.7-.5s.1-1.1.1-1.4c0-1.5.4-1.2 1.8-1.6.3-.1 1.3-.5 1.5-.4s.6.9.8 1.2c.3.4.4.8.8.9.6-.1 1.1-.2 1.6-.5l-3.5-5.3-2.1-3.1zm-.9 5.9c0-1.4 0-2.7.1-4.1l2.2 3.5-2.3.6zM35.5 36.4c-.4-1.4-.9-2.7-1.4-4.1-.2-.5-1-3.3-1.7-2.5-.3.4.6 3.6.7 4 .3 1.4.6 2.7 1 4.1.7 2.7 1.5 5.4 2.5 8 .2.4 1 3.2 1.6 2.3.2-.2-.2-1.2-.2-1.5-.2-1-.3-1.9-.5-2.9-.6-2.5-1.3-5-2-7.4zM56.6 48.8c-1.2.3-1.9.2-2.1 1.4s-.4 2.5-.6 3.7l-2.5-2.8c-.9-1-1.3-.5-2.6-.1 1.4 1.5 3 3 4.3 4.6 1 1.2 1.2 3.2 1.6 4.7.3-.1 1.5-.2 1.7-.5s-.2-1-.3-1.2c-.4-1-.7-2.1-.9-3.2.1-1.2.4-2.5.8-3.6.2-.9.4-1.9.6-3zM66 49.1l.9 3.3 1.2 4.2 1.7-.5-.9-3.1c0-.2-.4-1-.3-1.1.3-.5 1.6-.1 1.9.1 1.1.7 1.7 2.2 2.8 2.8.7.4 1.5-.1 2.3-.3-1.2-1.3-2.4-3.1-4.1-3.7 1.5-.8 2-2.6 1.2-4.1 0-.1-.1-.2-.1-.2-.9-1.6-2.2-1.5-3.8-1.1-.9.2-1.9.5-2.8.8-.7.2-.5 0-.6.6.1.8.3 1.6.6 2.3zm5.2-1.1c.5 1.8-1.9 2-3 2.3-.2-.9-.5-1.7-.7-2.6 1.1-.4 3.2-1.5 3.7.3zM64.8 57.5c.3-.1 1.7-.3 1.8-.5s-.4-1.4-.5-1.7l-4.7 1.3c-.2-.6-.9-1.9-.7-2.5s.7-.5 1.3-.6 2.4-.3 2.7-.8-.3-1.3-.4-1.7c-1.2.4-2.4.8-3.7 1-.7.1-.3.2-.7-.3-.2-.6-.4-1.2-.5-1.8l3.5-1c.2-.1.9-.2 1-.3s-.4-1.4-.5-1.7l-6.2 1.7 2.9 10.2 4.7-1.3z"
  })));
}

var _style$1, _path$1, _path2$1, _path3$1;

function _extends$4() {
  _extends$4 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$4.apply(this, arguments);
}

function SvgBlackjack(props) {
  return /*#__PURE__*/createElement("svg", _extends$4({
    id: "blackjack_svg__Lag_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: 0,
    y: 0,
    viewBox: "0 0 110 110",
    xmlSpace: "preserve"
  }, props), _style$1 || (_style$1 = /*#__PURE__*/createElement("style", null, ".blackjack_svg__st1{fill:#fff}")), _path$1 || (_path$1 = /*#__PURE__*/createElement("path", {
    fill: "none",
    d: "M.5-.1h110v110H.5z"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/createElement("path", {
    className: "blackjack_svg__st1",
    d: "M93.8 11H53.9c-1.9 0-3.3 1.5-3.3 3.4v13.2H15.3c-1.8 0-3.3 1.5-3.3 3.4v63.4c0 1.9 1.5 3.3 3.4 3.3h39.8c1.9 0 3.3-1.5 3.3-3.3V30.9c0-1.9-1.5-3.4-3.3-3.4h-2.5V14.4c0-.6.5-1.2 1.2-1.2h39.9c.6 0 1.2.5 1.2 1.2v63.4c0 .6-.5 1.2-1.2 1.2H58.6v2.2h35.2c1.9 0 3.3-1.5 3.3-3.3V14.4c0-1.9-1.5-3.4-3.3-3.4zM55.2 29.8c.6 0 1.2.5 1.2 1.2v63.4c0 .6-.5 1.2-1.2 1.2H15.3c-.6 0-1.2-.5-1.2-1.2V30.9c0-.6.5-1.2 1.2-1.2h39.9z"
  })), _path3$1 || (_path3$1 = /*#__PURE__*/createElement("path", {
    className: "blackjack_svg__st1",
    d: "M34.4 59.9h-1.9l.5-2.8h4.4l.6 2.7h-1.7v3.3h7v-3.3h-1.7l-4.5-20.6h-3.7l-4.5 20.5h-1.6V63h7.1v-3.1zm.9-13.9l1.6 8h-3.2l1.6-8zM35.4 70.5c-1.9 1.9-3.6 3.9-5.2 6.1-2.4 3.6-1.2 7 1.6 7.2 1.3 0 2.4-.8 2.6-2.1.1-.4.3-.3.3-.3v.2c.1 1.6-.3 3.3-1.2 4.7 1.1-.3 2.3-.3 3.5 0-.8-1.4-1.2-3.1-1.1-4.7v-.2s.2-.1.3.3c.2 1.2 1.4 2.1 2.6 2.1 2.9-.2 4-3.6 1.7-7.2-1.4-2.2-3.1-4.3-5.1-6.1zM74.6 64.7c.2 1.2 1.4 2.1 2.6 2.1 2.9-.2 4.1-3.6 1.7-7.2-1.5-2.2-3.3-4.3-5.2-6.1-1.9 1.9-3.6 3.9-5.2 6.1-2.4 3.6-1.2 7 1.6 7.2 1.3 0 2.4-.8 2.6-2.1.1-.4.3-.3.3-.3v.2c0 1.6-.4 3.3-1.2 4.7 1.1-.3 2.3-.3 3.5 0-.8-1.4-1.2-3.1-1.2-4.7v-.2c.2 0 .4-.1.5.3zM66.9 39.5v.8S66.6 47 73 47c5.8-.1 5.7-6.7 5.7-6.7v-15h1.6v-2.6h-6.4v2.5h1.6v14.9s0 3.6-2.6 3.6-2.8-3.5-2.8-3.5v-.8h-3.2z"
  })));
}

var _style$2, _path$2, _path2$2, _path3$2;

function _extends$5() {
  _extends$5 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$5.apply(this, arguments);
}

function SvgPoker(props) {
  return /*#__PURE__*/createElement("svg", _extends$5({
    id: "poker_svg__Lag_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: 0,
    y: 0,
    viewBox: "0 0 110 110",
    xmlSpace: "preserve"
  }, props), _style$2 || (_style$2 = /*#__PURE__*/createElement("style", null, ".poker_svg__st1{fill:#fff}")), _path$2 || (_path$2 = /*#__PURE__*/createElement("path", {
    fill: "none",
    d: "M.5-.1h110v110H.5z"
  })), _path2$2 || (_path2$2 = /*#__PURE__*/createElement("path", {
    className: "poker_svg__st1",
    d: "M86.8 28.9l10.1 3.7c3.6 1.3 5.4 5.3 4.1 8.9L82.2 93.1c-1.3 3.6-5.3 5.4-8.9 4.1L36.4 83.8c-1.6-.6-2.9-1.7-3.7-3.2.9.6 1.9.9 3.1 1l1.6.1 36.7 13.4c2.4.9 5.2-.4 6-2.8l18.8-51.7c.9-2.4-.4-5.2-2.8-6l-9.5-3.5.2-2.2z",
    transform: "translate(5.002 7.596)"
  })), _path3$2 || (_path3$2 = /*#__PURE__*/createElement("path", {
    className: "poker_svg__st1",
    d: "M50.9 8.6l11.1-2c3.8-.7 7.3 1.8 8 5.6l.6 3.4 10.2.9c3.8.3 6.6 3.7 6.3 7.5l-4.8 54.8c-.3 3.8-3.7 6.6-7.5 6.3l-39.1-3.4c-.6-.1-1.1-.2-1.6-.3-1.3 0-2.6-.4-3.7-1.1-2.7.1-5.3-1.4-6.5-4L.6 26.4C-1 23 .5 18.9 4 17.3L39.6.6C43.1-1 47.2.5 48.8 4l2.1 4.6zm1 2.1L72 53.8c1.6 3.5.1 7.6-3.3 9.2L34.1 79.2c.3 0 .6 0 .8-.1l38.7-6.8c2.6-.5 4.3-2.9 3.8-5.5l-9.6-54.2c-.5-2.6-2.9-4.3-5.5-3.8l-10.4 1.9zM42.1 80l33 2.9c2.6.2 4.9-1.7 5.1-4.3L85 23.9c.2-2.6-1.7-4.9-4.3-5.1L71 18l8.6 48.5c.7 3.8-1.8 7.3-5.6 8L42.1 80zM40.5 2.6L4.9 19.2c-2.4 1.1-3.4 3.9-2.3 6.3l23.2 49.8c1.1 2.4 3.9 3.4 6.3 2.3L67.8 61c2.4-1.1 3.4-3.9 2.3-6.3L46.8 4.9c-1.1-2.3-3.9-3.4-6.3-2.3zm51.1 38.2c-.5.3-.8.5-1.4.4-1.1-.2-1.9-1.3-1.4-2.5.3-.9 1.3-1.6 2.2-1.2 0 0 .4.3.6.2.3-.1 0-.4.2-.8.3-1 1.4-1.4 2.3-1.1.9.4 1.4 1.3 1 2.3-.2.4-.5.5-.4.7.1.2.5.2.6.2.9.3 1.2 1.5.9 2.3-.4 1.2-1.7 1.5-2.7 1-.5-.3-.6-.7-.8-1.2-.2-.5-.3-.2-.4.1-.4 1.6-.1 2.2-.1 2.2s-.4-.3-.8-.4c-.3-.1-.6-.2-.9-.2.6-.3 1.2-1.6 1.3-1.8s.2-.5-.2-.2zM79.3 29.6L76.6 25l3.5-4 2.7 4.6-3.5 4zM65.8 13.4c.2 1.4-1.1 3.3-2.7 5.2-2.1-1.3-4-2.6-4.3-4-.2-1.1.5-2.1 1.6-2.3.6-.1 1.3.4 1.8.8.3-.5.8-1.3 1.4-1.4 1.1-.1 2 .6 2.2 1.7zM26.5 41.2c.8-4.8 2.1-9.5 3.8-14.1 4.6 1.6 9 3.7 13.2 6.1 6.7 4.1 7.3 10.6 2.8 13-2.1 1-4.6.4-5.9-1.4-.4-.5-.7-.2-.7-.2l.1.2c1.2 2.7 3.1 5.1 5.5 6.9-2.1.4-4 1.3-5.7 2.6.2-3-.4-6-1.7-8.6-.1-.1-.1-.2-.1-.2s-.4 0-.3.7c.6 2.2-.6 4.6-2.7 5.5-4.8 1.9-9.4-2.8-8.3-10.5zm12-31.2c.3-1.5.7-3 1.2-4.5 1.5.5 2.9 1.2 4.2 1.9 2.1 1.3 2.3 3.3.9 4.1-.7.3-1.4.1-1.9-.5-.1-.2-.2-.1-.2-.1v.1c.4.9 1 1.6 1.7 2.2-.7.1-1.3.4-1.8.8.1-.9-.1-1.9-.5-2.7v-.1s-.1 0-.1.2c.2.7-.2 1.4-.9 1.7-1.5.8-3-.6-2.6-3.1zM27.8 70.8c.3-1.5.7-3 1.2-4.5 1.5.5 2.9 1.2 4.2 1.9 2.1 1.3 2.3 3.3.9 4.1-.7.3-1.4.1-1.9-.5-.1-.2-.2-.1-.2-.1v.1c.4.9 1 1.6 1.7 2.2-.7.1-1.3.4-1.8.8.1-.9-.1-1.9-.5-2.7V72s-.1 0-.1.2c.2.7-.2 1.4-.9 1.7-1.5.8-2.9-.6-2.6-3.1z",
    transform: "translate(5.002 7.596)"
  })));
}

var _path$3, _path2$3;

function _extends$6() {
  _extends$6 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$6.apply(this, arguments);
}

function SvgRoulette(props) {
  return /*#__PURE__*/createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 110 110"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/createElement("path", {
    fill: "none",
    d: "M.5-.1h110v110H.5z"
  })), _path2$3 || (_path2$3 = /*#__PURE__*/createElement("path", {
    d: "M55 101.2C29.5 101.2 8.8 80.5 8.8 55S29.5 8.8 55 8.8s46.2 20.7 46.2 46.2-20.7 46.2-46.2 46.2zm0-2.2c24.3 0 44-19.7 44-44S79.3 11 55 11 11 30.7 11 55s19.7 44 44 44zm19.7-77.1c.1 0 5.9 1.2 8.6 7.2l-7.7 4.8c-2-2.4-4.2-3.6-4.3-3.6l3.4-8.4zm18.7 33.4c0 .1 2 5.7-1.9 11l-8-4.2c1.1-3 1-5.4 1-5.5l8.9-1.3zM62.9 83.1c3.1-.5 5.2-1.9 5.2-1.9l5.6 7.1c-.1.1-3.9 4.5-10.5 3.8l-.3-9zm-28.4-7.4c2 2.4 4.2 3.6 4.3 3.6l-3.4 8.4c-.1 0-5.9-1.2-8.6-7.2l7.7-4.8zM18.7 43.2l8 4.2c-1.1 3-1 5.4-1 5.5l-9 1.3c0-.1-1.9-5.6 2-11zm17.7-21.9c.1-.1 3.9-4.5 10.5-3.8l.3 9.1c-3.1.5-5.2 1.9-5.2 1.9l-5.6-7.2zm-7.1 5.3l4.8 7.7c-2.4 2-3.6 4.2-3.6 4.3l-8.4-3.4c.1-.2 1.2-5.9 7.2-8.6zm2 7.3l-2.7-4.4c-.9.6-1.6 1.3-2.2 2.1-.6.7-1.1 1.5-1.4 2.3l4.8 1.9c.3-.6.9-1.3 1.5-1.9zm-4.5 28.8c.5 3.1 1.9 5.2 1.9 5.2l-7.1 5.6c-.1-.1-4.5-3.9-3.8-10.5l9-.3zm-.9 4.6c-.3-.7-.6-1.5-.8-2.4l-5.2.2c.1 1.8.7 3.4 1.6 4.8.1.2.2.4.4.5 0 .1 4-3.1 4-3.1zm21.9 15.9c2.4.9 4.5 1 5.2 1h.3l1.3 9c-.1 0-1.5.5-3.5.5-2.1 0-4.8-.5-7.5-2.4l4.2-8.1zm3.5 3.1c-.8-.1-1.6-.2-2.5-.4l-2.4 4.6c1.5.7 3 1 4.6 1 .4 0 .7 0 1.1-.1l-.8-5.1zm28.3-15.2l8.4 3.4c0 .1-1.2 5.9-7.2 8.6L76 75.3c2.4-2 3.6-4.2 3.6-4.2zm5.6 4.6l-4.8-1.9c-.5.6-1 1.3-1.6 1.9l2.7 4.4c.9-.6 1.6-1.3 2.2-2.1.7-.7 1.2-1.5 1.5-2.3zm3.3-39.6c.1.1 4.5 3.9 3.8 10.5l-9.1.3c-.5-3.1-1.9-5.2-1.9-5.2l7.2-5.6zm.1 3.6c-.1-.2-.2-.4-.4-.5l-4 3.2c.3.7.6 1.5.8 2.4l5.2-.2c-.1-1.9-.7-3.5-1.6-4.9zM59.1 16c2.1 0 4.8.5 7.5 2.4l-4.2 8c-2.4-.9-4.5-1-5.2-1h-.3l-1.3-9c.1 0 1.5-.4 3.5-.4zm2.2 7.8l2.4-4.6c-1.5-.7-3-1-4.6-1-.4 0-.7 0-1.1.1l.7 5.1c.9 0 1.7.1 2.6.4zm-1.9 40.7c1.4 0 2.8.9 3.3 2.3.7 1.8-.3 3.8-2.1 4.5-1.9.7-3.9-.3-4.6-2.1-.5-1.5 0-3.1 1.2-4l-1.6-4.3c-2.5.4-5-.7-6.2-2.8l-4 1.5c.1 1.5-.8 2.9-2.3 3.5-1.8.7-3.8-.3-4.5-2.1-.7-1.8.3-3.8 2.1-4.5 1.4-.5 2.9-.1 3.9 1l4.1-1.5c-.4-2.4.8-4.9 2.9-6.1L50 45.5c-1.5.1-2.9-.8-3.5-2.3-.7-1.8.3-3.8 2.1-4.5 1.8-.7 3.8.3 4.5 2.1.5 1.4.1 2.9-1 3.9l1.6 4.3c2.4-.4 4.8.8 6 2.8l4.6-1.7c0-1.4.9-2.8 2.3-3.3 1.8-.7 3.8.3 4.5 2.1.7 1.8-.3 3.8-2.1 4.5-1.5.5-3.1 0-4-1.2l-4.6 1.7c.4 2.4-.7 4.8-2.7 6.1l1.7 4.5z",
    fill: "#fff"
  })));
}

var DANSK_ROULETTE_ID = detectMobile() && !detectIpad() ? 3265 : 2101;
var DOUBLE_BALL_ROULETTE = 'double_ball_roulette';
var OPEN_HOURS_TYPE = 'Bounded';
var providersThumbsImgs = {
  dkRouletteEvo: 'dansk-roulette.jpeg',
  dkRouletteAG: 'arhus-roulette.jpeg',
  evoLobby: 'evolution-lobby.jpeg',
  AGLobby: 'authentic-lobby.jpeg'
};
var PROVIDERS_LOGOS = {
  evolution: /*#__PURE__*/React__default.createElement(SvgEvolutionIcon, null),
  authentic: /*#__PURE__*/React__default.createElement(SvgAuthenticIcon, null)
};
var PROVIDERS_THUMBS = [{
  gameId: ENV() === 'dev' ? 3191 : DANSK_ROULETTE_ID,
  title: {
    topTitle: 'Dansk',
    middleTitle: 'Roulette',
    bottomTitle: '',
    titlePosition: 'right'
  },
  bg: IMG_PATH + providersThumbsImgs.dkRouletteEvo,
  showFlag: true,
  providerLogo: PROVIDERS_LOGOS.evolution,
  providerName: 'evolution',
  providerKey: 'evolution',
  players: true,
  betLimits: true,
  showTimer: true
}, {
  gameId: 3188,
  title: {
    topTitle: 'Dansk',
    middleTitle: 'Roulette',
    bottomTitle: '',
    titlePosition: 'right'
  },
  bg: IMG_PATH + providersThumbsImgs.dkRouletteAG,
  showFlag: true,
  providerLogo: PROVIDERS_LOGOS.authentic,
  providerName: 'authentic',
  providerKey: 'authentic',
  players: true,
  betLimits: true,
  showTimer: true
}, {
  gameId: '',
  title: {
    topTitle: 'Lobby',
    middleTitle: 'Live Casino',
    bottomTitle: 'åbne borde',
    titlePosition: 'left'
  },
  bg: IMG_PATH + providersThumbsImgs.evoLobby,
  showFlag: false,
  providerLogo: PROVIDERS_LOGOS.evolution,
  providerName: 'evolution',
  providerKey: 'evolution',
  players: false,
  betLimits: false,
  showTimer: false
}, {
  gameId: '',
  title: {
    topTitle: 'Lobby',
    middleTitle: 'Authentic',
    bottomTitle: 'åbne borde',
    titlePosition: 'left'
  },
  bg: IMG_PATH + providersThumbsImgs.AGLobby,
  showFlag: false,
  providerLogo: PROVIDERS_LOGOS.evolution,
  providerName: 'authentic',
  providerKey: 'authentic',
  players: false,
  betLimits: false,
  showTimer: false
}];
var LIVE_CASINO_CATEGORIES = [{
  name: 'all',
  label: 'Alle',
  url: 'alle',
  icon: null,
  bgImage: 'banner-bg.jpeg',
  bgGradient: {},
  person: ''
}, {
  name: 'roulette',
  label: 'Roulette',
  url: 'roulette',
  icon: /*#__PURE__*/React__default.createElement(SvgRoulette, null),
  bgImage: '',
  bgGradient: {
    leftColor: '#206F47',
    rightColor: '#103824'
  },
  person: 'roulette.png'
}, {
  name: 'blackJack',
  label: 'Blackjack',
  url: 'blackJack',
  icon: /*#__PURE__*/React__default.createElement(SvgBlackjack, null),
  bgImage: '',
  bgGradient: {
    leftColor: '#6F2048',
    rightColor: '#381024'
  },
  person: 'blackjack.png'
}, {
  name: 'baccarat',
  label: 'Baccarat',
  url: 'baccarat',
  icon: /*#__PURE__*/React__default.createElement(SvgBaccarat, null),
  bgImage: '',
  bgGradient: {
    leftColor: '#6F4720',
    rightColor: '#382410'
  },
  person: 'baccarat.png'
}, {
  name: 'poker',
  label: 'Poker',
  url: 'poker',
  icon: /*#__PURE__*/React__default.createElement(SvgPoker, null),
  bgImage: '',
  bgGradient: {
    leftColor: '#20486F',
    rightColor: '#102438'
  },
  person: 'poker.png'
}];

var _templateObject$3, _templateObject2$2;
var TimerBoxWrapper = styled.div(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteralLoose(["\n    display: flex;\n    justify-content: space-between;\n    flex-direction: row;\n"])));
var TimerBox = styled.div(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteralLoose(["\n    width: 57px;\n    height: 57px;\n    line-height: 57px;\n    border-radius: 5px;\n    background: rgba(0, 0, 0, 0.8);\n    color: #fff;\n    font-size: 32px;\n    text-align: center;\n    vertical-align: middle;\n\n    @media (max-width: ", ") {\n        width: 37px;\n        height: 37px;\n        line-height: 37px;\n        font-size: 20px;\n    }\n"])), BREAKPOINTS.lg + 'px');

var renderer = function renderer(_ref) {
  var hours = _ref.hours,
      minutes = _ref.minutes,
      seconds = _ref.seconds,
      completed = _ref.completed;

  if (completed) {
    return null;
  } else {
    return /*#__PURE__*/React__default.createElement(TimerBoxWrapper, null, /*#__PURE__*/React__default.createElement(TimerBox, null, zeroPad(hours)), /*#__PURE__*/React__default.createElement(TimerBox, null, zeroPad(minutes)), /*#__PURE__*/React__default.createElement(TimerBox, null, zeroPad(seconds)));
  }
};

var CountdownTimer = function CountdownTimer(_ref2) {
  var startTime = _ref2.startTime,
      _ref2$completeCallbac = _ref2.completeCallback,
      completeCallback = _ref2$completeCallbac === void 0 ? function () {} : _ref2$completeCallbac,
      _ref2$onMountCallback = _ref2.onMountCallback,
      onMountCallback = _ref2$onMountCallback === void 0 ? function () {} : _ref2$onMountCallback;

  var convertToMS = function convertToMS(t) {
    return Number(t.split(':')[0]) * 60 * 60 * 1000 + Number(t.split(':')[1]) * 60 * 1000;
  };

  var subtractTime = function subtractTime(startTime) {
    var d = new Date();
    var curTime = d.getHours() + ":" + d.getMinutes();
    return convertToMS(startTime) - convertToMS(curTime);
  };

  return /*#__PURE__*/React__default.createElement(Countdown, {
    date: Date.now() + subtractTime(startTime),
    precision: 4,
    renderer: renderer,
    onComplete: function onComplete(timerStatus) {
      completeCallback(timerStatus.completed);
    },
    onMount: function onMount(status) {
      return Object.keys(status) ? onMountCallback(status.completed) : false;
    }
  });
};

CountdownTimer.propTypes = {
  startTime: PropTypes.string,
  completeCallback: PropTypes.func,
  onMountCallback: PropTypes.func
};

var _g$2;

function _extends$7() {
  _extends$7 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$7.apply(this, arguments);
}

function SvgAvatar(props) {
  return /*#__PURE__*/createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12
  }, props), _g$2 || (_g$2 = /*#__PURE__*/createElement("g", {
    opacity: 0.498
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Rectangle 3157",
    fill: "none",
    d: "M0 0h12v12H0z"
  }), /*#__PURE__*/createElement("g", {
    "data-name": "Group 3126",
    fill: "#fff"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Path 994",
    d: "M5.987 6.223a3 3 0 002.937-3.06 2.964 2.964 0 10-5.927.054 3.005 3.005 0 002.99 3.005z"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Path 995",
    d: "M11.094 8.601a2.835 2.835 0 00-.3-.554A3.675 3.675 0 008.23 6.428a.538.538 0 00-.376.1 3.1 3.1 0 01-3.728.034.478.478 0 00-.376-.089 3.651 3.651 0 00-2.535 1.666 3.274 3.274 0 00-.293.559.286.286 0 00.015.252 5.273 5.273 0 00.354.527 5.019 5.019 0 00.6.683 7.844 7.844 0 00.6.525 5.8 5.8 0 007.04-.064 5.715 5.715 0 00.589-.535 6.053 6.053 0 00.589-.694 4.665 4.665 0 00.345-.533.23.23 0 00.04-.258z"
  })))));
}

var _path$4, _g$3;

function _extends$8() {
  _extends$8 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$8.apply(this, arguments);
}

function SvgSeat(props) {
  return /*#__PURE__*/createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12
  }, props), _path$4 || (_path$4 = /*#__PURE__*/createElement("path", {
    "data-name": "Rectangle 3157",
    fill: "none",
    d: "M0 0h12v12H0z"
  })), _g$3 || (_g$3 = /*#__PURE__*/createElement("g", {
    "data-name": "Group 3126"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Path 994",
    d: "M5.987 6.223a3 3 0 002.937-3.06 2.964 2.964 0 10-5.927.054 3.005 3.005 0 002.99 3.005z"
  }), /*#__PURE__*/createElement("path", {
    "data-name": "Path 995",
    d: "M11.094 8.601a2.835 2.835 0 00-.3-.554A3.675 3.675 0 008.23 6.428a.538.538 0 00-.376.1 3.1 3.1 0 01-3.728.034.478.478 0 00-.376-.089 3.651 3.651 0 00-2.535 1.666 3.274 3.274 0 00-.293.559.286.286 0 00.015.252 5.273 5.273 0 00.354.527 5.019 5.019 0 00.6.683 7.844 7.844 0 00.6.525 5.8 5.8 0 007.04-.064 5.715 5.715 0 00.589-.535 6.053 6.053 0 00.589-.694 4.665 4.665 0 00.345-.533.23.23 0 00.04-.258z"
  }))));
}

var _templateObject$4, _templateObject2$3, _templateObject3$1, _templateObject4$1, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
var BET_CIRCLE_COLORS = {
  red: '#FD484C',
  black: '#333333',
  green: '#1CAC51',
  yellow: '#C49B53',
  blue: '#3A94FF'
};
var BET_CIRCLE_SIZES = {
  defaultSize: '30px',
  mobileSize: '12px'
};
var BottomDropDown = styled.div(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    height: ", ";\n    background: rgba(0, 0, 0, 0.8);\n    opacity: ", ";\n    border-radius: 0 0 8px 8px;\n    transition: all 0.3s ease;\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n    padding: 0 3px;\n    overflow-x: scroll;\n    -ms-overflow-style: none;\n    scrollbar-width: none;\n    * {\n        -webkit-user-select: none;\n        -moz-user-select: none;\n        -ms-user-select: none;\n        user-select: none;\n    }\n\n    ::-webkit-scrollbar {\n        display: none;\n    }\n\n    @media (max-width: ", ") {\n        height: ", ";\n    }\n"])), function (props) {
  return props.active ? props.extended ? '80px' : '40px' : '0';
}, function (props) {
  return props.active ? '1' : '0';
}, BREAKPOINTS.md + 'px', function (props) {
  return props.active ? props.extended ? '40px' : '20px' : '0';
});
var BetCircle = styled.div(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteralLoose(["\n    min-width: ", ";\n    height: ", ";\n    border-radius: ", ";\n    line-height: ", ";\n    text-align: center;\n    font-size: 14px;\n    font-weight: bold;\n    color: #fff;\n    vertical-align: center;\n    margin: 0 3px;\n    border: 1px solid ", ";\n    box-sizing: content-box;\n    background: ", ";\n\n    @media (max-width: ", ") {\n        min-width: ", ";\n        height: ", ";\n        border-radius: ", ";\n        line-height: ", ";\n        font-size: 8px;\n    }\n"])), BET_CIRCLE_SIZES.defaultSize, BET_CIRCLE_SIZES.defaultSize, BET_CIRCLE_SIZES.defaultSize, BET_CIRCLE_SIZES.defaultSize, function (props) {
  return props.border ? ' #fff' : 'transparent';
}, function (props) {
  return Object.keys(BET_CIRCLE_COLORS).includes(props.color) ? BET_CIRCLE_COLORS[props.color] : 'none';
}, BREAKPOINTS.md + 'px', BET_CIRCLE_SIZES.mobileSize, BET_CIRCLE_SIZES.mobileSize, BET_CIRCLE_SIZES.mobileSize, BET_CIRCLE_SIZES.mobileSize);
var DoubleRouletteCircle = styled.div(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteralLoose(["\n    display: flex;\n    flex-direction: column;\n    justify-content: space-evenly;\n"])));
var DoubleRouletteMerged = styled.div(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteralLoose(["\n    margin: 0 3px;\n    border-radius: 30px;\n    width: 30px;\n    background: ", ";\n    height: 100%;\n    align-items: center;\n    justify-content: space-evenly;\n    flex-direction: column;\n    display: flex;\n\n    @media (max-width: ", ") {\n        width: ", ";\n        height: ", ";\n        border-radius: ", ";\n    }\n"])), function (props) {
  return props.equal ? '#c49b53' : 'none';
}, BREAKPOINTS.md + 'px', BET_CIRCLE_SIZES.mobileSize, BET_CIRCLE_SIZES.mobileSize, BET_CIRCLE_SIZES.mobileSize);
var RouletteCircle = styled(BetCircle)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return props["double"] && css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteralLoose(["\n            margin: 0;\n            &:first-child {\n                margin-bottom: 2px;\n            }\n            &:last-child {\n                margin-top: 2px;\n            }\n        "])));
});
var BlackjackCircle = styled(BetCircle)(_templateObject7 || (_templateObject7 = _taggedTemplateLiteralLoose(["\n    background: ", ";\n"])), function (props) {
  return props.color ? BET_CIRCLE_COLORS.green : BET_CIRCLE_COLORS.black;
});
var BlackjackTakenSeat = styled(SvgSeat)(_templateObject8 || (_templateObject8 = _taggedTemplateLiteralLoose(["\n    filter: ", ";\n"])), function (props) {
  return props.taken ? 'invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)' : 'invert(42%) sepia(0%) saturate(0%) hue-rotate(186deg) brightness(104%) contrast(92%)';
});
var BlackjackBetBehind = styled.div(_templateObject9 || (_templateObject9 = _taggedTemplateLiteralLoose(["\n    color: #fff;\n    font-size: 14px;\n    text-align: center;\n    font-weight: bold;\n    width: 100%;\n"])));

var rouletteColors = {
  green: [0],
  red: [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36],
  black: [2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35]
};

var Roulette = function Roulette(_ref) {
  var liveCasinoData = _ref.liveCasinoData,
      active = _ref.active,
      privateTable = _ref.privateTable;
  var betResults = liveCasinoData.results !== null && liveCasinoData.results;
  var gameSubType = liveCasinoData.gameSubType;

  var resolveRouletteColor = function resolveRouletteColor(number) {
    return Object.keys(rouletteColors).find(function (el) {
      return rouletteColors[el].includes(number);
    });
  };

  var splitDoubleBall = function splitDoubleBall(number, position) {
    if (number === void 0) {
      number = '';
    }

    if (position === void 0) {
      position = 0;
    }

    var slitedArray = number.split('|');
    return slitedArray[position];
  };

  var resolveDoubleRouletteColor = function resolveDoubleRouletteColor(number, position) {
    if (number === void 0) {
      number = '';
    }

    return isDouble() && isEqualNumbers(number) ? null : resolveRouletteColor(+splitDoubleBall(number, position));
  };

  var isDouble = function isDouble() {
    return DOUBLE_BALL_ROULETTE === gameSubType;
  };

  var isEqualNumbers = function isEqualNumbers(number) {
    return splitDoubleBall(number) === splitDoubleBall(number, 1);
  };

  var isAllowedData = function isAllowedData() {
    return betResults.length && (typeof betResults[0] === 'number' || typeof betResults[0] === 'string');
  };

  return isAllowedData() ? /*#__PURE__*/React__default.createElement(BottomDropDown, {
    active: active,
    extended: DOUBLE_BALL_ROULETTE === gameSubType,
    gameSubType: gameSubType,
    privateTable: privateTable
  }, betResults.map(function (number, index) {
    return isDouble() ? /*#__PURE__*/React__default.createElement(DoubleRouletteCircle, {
      key: index
    }, /*#__PURE__*/React__default.createElement(DoubleRouletteMerged, {
      equal: isEqualNumbers(number)
    }, /*#__PURE__*/React__default.createElement(RouletteCircle, {
      color: resolveDoubleRouletteColor(number, 0),
      border: index < 1,
      "double": isDouble()
    }, splitDoubleBall(number)), /*#__PURE__*/React__default.createElement(RouletteCircle, {
      color: resolveDoubleRouletteColor(number, 1),
      border: index < 1,
      key: index,
      "double": isDouble()
    }, splitDoubleBall(number, 1)))) : /*#__PURE__*/React__default.createElement(RouletteCircle, {
      color: resolveRouletteColor(+number),
      border: index < 1,
      key: index
    }, number);
  })) : null;
};

var Blackjack = function Blackjack(_ref) {
  var liveCasinoData = _ref.liveCasinoData,
      active = _ref.active,
      privateTable = _ref.privateTable;
  var seats = liveCasinoData.seats === null ? 0 : liveCasinoData.seats;
  var seatsTaken = liveCasinoData.seatsTaken === null ? [] : liveCasinoData.seatsTaken;
  var arraySeats = Array.from(Array(seats).keys());

  var isSeatTaken = function isSeatTaken(number) {
    return seatsTaken.includes(number);
  };

  var isAllowedData = function isAllowedData() {
    return typeof seats === 'number' && Array.isArray(seatsTaken);
  };

  return isAllowedData() && /*#__PURE__*/React__default.createElement(BottomDropDown, {
    active: active,
    privateTable: privateTable
  }, seatsTaken ? seatsTaken.length === seats ? /*#__PURE__*/React__default.createElement(BlackjackBetBehind, null, "Bet behind") : arraySeats.map(function (number, index) {
    return /*#__PURE__*/React__default.createElement(BlackjackCircle, {
      key: index,
      color: isSeatTaken(number)
    }, /*#__PURE__*/React__default.createElement(BlackjackTakenSeat, {
      taken: isSeatTaken(number)
    }));
  }) : null);
};

var baccaratColors = {
  T: 'green',
  B: 'red',
  P: 'blue'
};

var Baccarat = function Baccarat(_ref) {
  var _ref$history = _ref.history,
      history = _ref$history === void 0 ? [] : _ref$history,
      active = _ref.active,
      privateTable = _ref.privateTable;

  var resolveBaccaratColor = function resolveBaccaratColor(key) {
    return baccaratColors[key];
  };

  var isAllowedData = function isAllowedData() {
    return history && Array.isArray(history) && history.length > 0;
  };

  return isAllowedData() && /*#__PURE__*/React__default.createElement(BottomDropDown, {
    active: active,
    privateTable: privateTable
  }, history.map(function (value, index) {
    return /*#__PURE__*/React__default.createElement(BetCircle, {
      key: index,
      border: index < 1,
      color: resolveBaccaratColor(value)
    }, value);
  }));
};

var _templateObject$5, _templateObject2$4, _templateObject3$2, _templateObject4$2;
var BottomBox = styled.div(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    position: absolute;\n    bottom: 0;\n"])));
var BottomBoxData = styled.div(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    display: flex;\n    justify-content: ", ";\n    padding: 0 5px 5px;\n    box-sizing: border-box;\n"])), function (props) {
  return props.showPlayersCount ? 'space-between' : 'flex-end';
});
var UsersCount = styled(HighlightBox)(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteralLoose(["\n    svg {\n        display: inline-block;\n        margin: 0 5px 0 0;\n        vertical-align: middle;\n\n        @media (max-width: ", ") {\n            vertical-align: top;\n            margin-top: 3px;\n        }\n    }\n"])), BREAKPOINTS.md + 'px');
var BetRange = styled(HighlightBox)(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteralLoose(["\n    @media (max-width: ", ") {\n        display: none;\n    }\n"])), BREAKPOINTS.lg + 'px');

var BottomBar = function BottomBar(_ref) {
  var liveCasinoData = _ref.liveCasinoData,
      showPlayersCount = _ref.showPlayersCount,
      _ref$subprovider = _ref.subprovider,
      subprovider = _ref$subprovider === void 0 ? '' : _ref$subprovider,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? false : _ref$active,
      _ref$gameType = _ref.gameType,
      gameType = _ref$gameType === void 0 ? '' : _ref$gameType,
      _ref$privateTable = _ref.privateTable,
      privateTable = _ref$privateTable === void 0 ? false : _ref$privateTable;

  var convertCurrency = function convertCurrency(currency) {
    return currency.toLocaleString('da-DK');
  };

  var seats = typeof liveCasinoData.seats === 'number' ? liveCasinoData.seats : 0;
  var seatsTaken = liveCasinoData.seatsTaken && Array.isArray(liveCasinoData.seatsTaken) ? liveCasinoData.seatsTaken : [];
  var players = seats ? seatsTaken.length + '/' + seats : liveCasinoData.players;

  var showDropDownByType = function showDropDownByType() {
    if (!privateTable && subprovider === 'evolution') {
      return liveCasinoData.open;
    }

    return false;
  };

  var betLimits = function betLimits() {
    var _liveCasinoData$priva, _liveCasinoData$betLi, _liveCasinoData$betLi2, _liveCasinoData$betLi3;

    if (privateTable && (_liveCasinoData$priva = liveCasinoData.privateTableConfig) !== null && _liveCasinoData$priva !== void 0 && _liveCasinoData$priva.minBetsCount && (_liveCasinoData$betLi = liveCasinoData.betLimits) !== null && _liveCasinoData$betLi !== void 0 && _liveCasinoData$betLi.symbol) {
      return liveCasinoData.privateTableConfig.minBetsCount + ' ' + liveCasinoData.betLimits.symbol;
    } else if ((_liveCasinoData$betLi2 = liveCasinoData.betLimits) !== null && _liveCasinoData$betLi2 !== void 0 && _liveCasinoData$betLi2.min && (_liveCasinoData$betLi3 = liveCasinoData.betLimits) !== null && _liveCasinoData$betLi3 !== void 0 && _liveCasinoData$betLi3.max) {
      return convertCurrency(liveCasinoData.betLimits.min) + " - " + convertCurrency(liveCasinoData.betLimits.max) + " " + liveCasinoData.betLimits.symbol;
    } else {
      return false;
    }
  };

  var showGameTableByType = function showGameTableByType(type) {
    switch (type) {
      case 'roulette':
        return /*#__PURE__*/React__default.createElement(Roulette, {
          liveCasinoData: liveCasinoData,
          active: detectMobile() ? true : active,
          privateTable: privateTable
        });

      case 'blackjack':
        return /*#__PURE__*/React__default.createElement(Blackjack, {
          liveCasinoData: liveCasinoData,
          active: detectMobile() ? true : active,
          privateTable: privateTable
        });

      case 'baccarat':
        return /*#__PURE__*/React__default.createElement(Baccarat, {
          history: liveCasinoData.history,
          active: detectMobile() ? true : active
        });
    }
  };

  return /*#__PURE__*/React__default.createElement(BottomBox, null, /*#__PURE__*/React__default.createElement(BottomBoxData, {
    showPlayersCount: showPlayersCount
  }, showPlayersCount && /*#__PURE__*/React__default.createElement(UsersCount, null, /*#__PURE__*/React__default.createElement(SvgAvatar, null), players), betLimits() && /*#__PURE__*/React__default.createElement(BetRange, null, betLimits())), showDropDownByType() ? showGameTableByType(gameType) : null);
};

var post = function post(url, data, setting) {
  try {
    var options = _extends({
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(data)
    }, setting);

    return Promise.resolve(fetch(url, options)).then(function (response) {
      return response.json();
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
var get = function get(url, data, setting) {
  if (data === void 0) {
    data = {};
  }

  try {
    var options = _extends({
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      }
    }, setting);

    return Promise.resolve(fetch(url + (Object.keys(data).length ? '?' + new URLSearchParams(data) : ''), options)).then(function (response) {
      return response.json();
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

var AppContext = createContext({
  userInfo: {
    userId: '',
    status: ''
  },
  skin: {},
  notification: function notification() {
    return {
      text: '',
      type: ''
    };
  }
});

var playGame = function playGame(options, callback) {
  getGameLaunchUrl(options).then(function (apiUrl) {
    apiUrl ? callback(apiUrl) : null;
  });
};

var start = function start(game, userInfo, skin, callback) {
  if (Object.keys(userInfo).length) {
    var options = _extends({
      game_id: game.game_id,
      server_mode: ENV(),
      mode: 'money',
      srv: ENV(),
      pngDiv: game.game_id
    }, skin);

    playGame(options, callback);
  }
};

var getGameLaunchUrl = function getGameLaunchUrl(options) {
  return post(CONTENT_API_BASE_URL + 'core/getSes.php', '', {
    credentials: 'include'
  }).then(function (data) {
    return getLaunchLink(_extends({}, options, {
      session_id: 'JSESSIONID=' + data.sessId
    }));
  })["catch"](function (err) {
    console.error(err.toString());
    return err.toString();
  });
};

var getLaunchLink = function getLaunchLink(options) {
  var requestParams = _extends({}, options, {
    lobby: detectMobile() && !detectIpad() ? window.location.origin : window.location.origin + '/lobby'
  });

  if (!detectMobile() || detectIpad()) {
    requestParams.platform = 'web';
  }

  return post(GAME_LAUNCH_API + 'launch/rod25', requestParams).then(function (res) {
    if (res.error) {
      console.error(res.error.message);
    }

    if (res.data) {
      return res.data.url;
    }
  })["catch"](function (error) {
    console.error(error.toString());
    return error.toString();
  });
};

var LaunchGame = function LaunchGame(game, userInfo, skin) {
  if (game.subprovider === 'evolution') {
    start(game, userInfo, skin, function (link) {
      window.location.href = link;
    });
    return;
  }
};

var LaunchLobby = function LaunchLobby(skin, category) {
  if (category === void 0) {
    category = 'top_games';
  }

  return post(CONTENT_API_BASE_URL + 'core/getSes.php', '', {
    credentials: 'include'
  }).then(function (data) {
    return getEvoCategoryLink(_extends({
      app: APP_NAME,
      server_mode: ENV(),
      srv: ENV(),
      category: category,
      session_id: 'JSESSIONID=' + data.sessId
    }, skin));
  })["catch"](function (err) {
    console.error(err.toString());
    return err.toString();
  });
};

var getEvoCategoryLink = function getEvoCategoryLink(options) {
  return post(API_URL + 'categories/evolution', options).then(function (res) {
    if (res.error) {
      console.error(res.error.message);
    }

    if (res.data) {
      var link = res.data.url;
      return link;
    }
  })["catch"](function (error) {
    console.error(error.toString());
    return error.toString();
  });
};

var GameLaunchService = {
  LaunchLobby: LaunchLobby,
  LaunchGame: LaunchGame
};

var convertTimeWithZone = function convertTimeWithZone(time) {
  var d = new Date();

  var _time$split = time.split(':'),
      timeHours = _time$split[0],
      timeMins = _time$split[1];

  d.setUTCHours(timeHours, timeMins);
  return d.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  });
};

var _image;

function _extends$9() {
  _extends$9 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$9.apply(this, arguments);
}

function SvgDkFlag(props) {
  return /*#__PURE__*/createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: 28,
    height: 20
  }, props), _image || (_image = /*#__PURE__*/createElement("image", {
    width: 28,
    height: 20,
    href: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAUCAMAAACpgK3LAAAABGdBTUEAALGPC/xhBQAAACBjSFJN AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAclBMVEUqAADGCDHGCzDGCy/W aoDt7e3goazECDHFCC/XaoDhoa3CCC/FDDDGDDDhprPnxczp19vVZHvfnqzlwcjFDDDGDDDGCy/H Cy+3CSPGDDC2CSMAAACWByLGDDDGDDDXa4Dt7e3goa2YByLGDDDu7u7///91EANoAAAAI3RSTlMG ft/08vXxfoP8+4Lr6vr7/Pb4+fz7zcxY/FcBS9bx7vLuSqi3nQ0AAAABYktHRCXDAckPAAAAB3RJ TUUH5gQEFCcMvneWSgAAAGNJREFUKM/FzzcOgDAQBdFPzjlng/f+Z0QWLgBpaXntVAMYpqXYjuNa d6YH+PISEIXyKULMxwSSj/KXmGoZUZ6+gD58x0Iriari5ZfPmo8NWj526AcuDiOmeVmVTYh9vVuO 6QTS5EFpcVVnFAAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyMi0wNC0wNFQxNzozOToxMiswMzowMNOe +qsAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjItMDQtMDRUMTc6Mzk6MTIrMDM6MDCiw0IXAAAAAElF TkSuQmCC"
  })));
}

var _templateObject$6, _templateObject2$5, _templateObject3$3, _templateObject4$3;
var FlagBlock = styled.div(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteralLoose(["\n    line-height: 20px;\n    display: inline-block;\n    position: absolute;\n    top: 5px;\n    left: 5px;\n    z-index: 1;\n\n    * {\n        display: inline-block;\n    }\n"])));
var DKflagStyled = styled(SvgDkFlag)(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteralLoose(["\n    width: auto;\n    height: 17px;\n    image {\n        @media (max-width: ", ") {\n            width: auto;\n            height: 17px;\n        }\n    }\n"])), BREAKPOINTS.md + 'px');
var ThumbProvider = styled(HighlightBox)(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteralLoose(["\n    vertical-align: top;\n    margin: 0 0 0 5px;\n    text-transform: capitalize;\n"])));
var ThumbProviderIcon = styled.div(_templateObject4$3 || (_templateObject4$3 = _taggedTemplateLiteralLoose(["\n    margin: 0 5px 0 0;\n    line-height: 20px;\n    vertical-align: middle;\n"])));

var FlagBar = function FlagBar(_ref) {
  var _ref$showFlag = _ref.showFlag,
      showFlag = _ref$showFlag === void 0 ? false : _ref$showFlag,
      providerName = _ref.providerName;
  return /*#__PURE__*/React__default.createElement(FlagBlock, null, showFlag && /*#__PURE__*/React__default.createElement(DKflagStyled, null), /*#__PURE__*/React__default.createElement(ThumbProvider, null, PROVIDERS_LOGOS[providerName] && /*#__PURE__*/React__default.createElement(ThumbProviderIcon, null, PROVIDERS_LOGOS[providerName]), providerName));
};

var isObject = function isObject(el) {
  return el && el.constructor.name === 'Object';
};

var _templateObject$7, _templateObject2$6, _templateObject3$4, _templateObject4$4, _templateObject5$1, _templateObject6$1, _templateObject7$1;
var StyledThumbWrapper = styled.div(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteralLoose(["\n    width: 50%;\n    height: auto;\n    margin: 20px 0 0;\n    box-sizing: border-box;\n    padding: 0 10px;\n\n    @media (max-width: ", ") {\n        margin: 10px 0 0;\n        padding: 0 5px;\n    }\n\n    @media (max-width: ", ") {\n        width: 100%;\n        margin: 10px 0 0;\n        padding: 0 5px;\n    }\n"])), BREAKPOINTS.md + 'px', BREAKPOINTS.sm + 'px');
var StyledThumb = styled.div(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteralLoose(["\n    transition: all 0.3s ease 0s;\n    position: relative;\n    border-radius: 8px;\n    overflow: hidden;\n    line-height: 0; //removing space between picture and border\n\n    :hover {\n        cursor: ", ";\n        transform: scale(1.05);\n    }\n\n    img {\n        width: 100%;\n        min-height: 100%;\n        object-fit: contain;\n        box-sizing: content-box;\n        transition: all 0.3s ease 0s;\n        border-radius: 8px;\n\n        ", ";\n    }\n\n    @media (max-width: ", ") {\n        width: 100%; //mob: 345/104\n        min-width: 354px;\n        height: auto; //deks: 544/174\n        min-height: 110px;\n    }\n"])), function (props) {
  return props.closed ? 'default' : 'pointer';
}, function (props) {
  return props.closed && css(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteralLoose(["\n                filter: brightness(50%);\n            "])));
}, BREAKPOINTS.sm + 'px');
var TitleBox = styled.div(_templateObject4$4 || (_templateObject4$4 = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    top: calc(50% - 55px);\n    left: 10%;\n    text-align: center;\n    color: #fff;\n\n    @media (max-width: ", ") {\n        top: calc(50% - 40px);\n    }\n\n    ", ";\n\n    h4 {\n        font-size: 32px;\n        line-height: 32px;\n        font-weight: normal;\n        margin: 0;\n\n        @media (max-width: ", ") {\n            font-size: 22px;\n            line-height: 22px;\n        }\n    }\n\n    h3 {\n        font-size: 48px;\n        line-height: 48px;\n        margin: 0;\n\n        @media (max-width: ", ") {\n            font-size: 32px;\n            line-height: 32px;\n        }\n    }\n"])), BREAKPOINTS.lg + 'px', function (props) {
  return props.titlePosition === 'right' && css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteralLoose(["\n            right: 10%;\n            left: initial;\n        "])));
}, BREAKPOINTS.lg + 'px', BREAKPOINTS.lg + 'px');
var TitleDesc = styled.p(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteralLoose(["\n    font-size: 16px;\n    line-height: 16px;\n    margin-top: 5px;\n    text-align: center;\n\n    @media (max-width: ", ") {\n        font-size: 12px;\n        line-height: 12px;\n    }\n"])), BREAKPOINTS.lg + 'px');
var TimeRange = styled.div(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    font-size: 16px;\n    color: #fff;\n    text-align: center;\n    margin: 5px 0 0;\n    line-height: 100%;\n\n    @media (max-width: ", ") {\n        font-size: 12px;\n    }\n"])), BREAKPOINTS.lg + 'px');

var ProvidersThumb = function ProvidersThumb(_ref) {
  var _thumb$liveCasino$wor, _thumb$liveCasino$wor2, _thumb$liveCasino$wor3;

  var liveCasinoGames = _ref.liveCasinoGames,
      thumb = _ref.thumb,
      thumbClickHandler = _ref.thumbClickHandler;

  var _useState = useState(false),
      finishedCountdown = _useState[0],
      setFinishedCountdown = _useState[1];

  var _useState2 = useState({}),
      game = _useState2[0],
      setGame = _useState2[1];

  var appContextData = useContext(AppContext);

  var showTimer = function showTimer() {
    return thumb.showTimer && !thumb.liveCasino.open && isObject(thumb.liveCasino.workHours.value) && !finishedCountdown && !!Object.keys(thumb.liveCasino).length;
  };

  var findGameById = function findGameById() {
    return liveCasinoGames.find(function (game) {
      return game.id === thumb.gameId;
    });
  };

  var providerGamesCount = function providerGamesCount(provider) {
    return liveCasinoGames.filter(function (game) {
      return game.subprovider === provider;
    }).length;
  };

  var isGameClosed = function isGameClosed() {
    return thumb.liveCasino.open === false && !finishedCountdown && isObject(thumb.liveCasino.workHours.value);
  };

  var getLobbyUrl = function getLobbyUrl(provider) {
    if (provider === 'evolution') GameLaunchService.LaunchLobby(appContextData.skin, 'top_games').then(function (apiUrl) {
      window.location.href = apiUrl;
    })["catch"](function (error) {
      appContextData.notification({
        text: 'Launch lobby:' + error.toString(),
        type: 'error'
      });
    });
  };

  var LaunchGame = function LaunchGame(game) {
    if (!isGameClosed()) {
      var userInfo = appContextData.userInfo;

      if (userInfo.status === 'open' || userInfo.status === 'temp') {
        GameLaunchService.LaunchGame(game, userInfo, appContextData.skin);
      } else {
        appContextData.notification({
          text: 'User is selfExcluded',
          type: 'selfExcluded'
        });
      }
    }
  };

  var onThumbClick = function onThumbClick(game) {
    var userInfo = appContextData.userInfo;

    if (!showTimer()) {
      if (userInfo && userInfo.userId) {
        if (userInfo.status === 'open' || userInfo.status === 'temp') {
          if (thumb.gameId && game === undefined) {
            console.error("game with ID - " + thumb.gameId + " wasn't match eny tableid in a system");
            return;
          } else {
            return game ? LaunchGame(game) : getLobbyUrl('evolution');
          }
        } else {
          appContextData.notification({
            text: 'User is selfExcluded',
            type: 'selfExcluded'
          });
        }
      } else {
        appContextData.notification({
          text: 'Du spiller nu for sjov. Opret en konto eller log ind for at spille for rigtige penge',
          type: 'warning'
        });
        return;
      }
    }
  };

  useEffect(function () {
    setGame(findGameById());
  }, []);
  return /*#__PURE__*/React__default.createElement(StyledThumbWrapper, null, /*#__PURE__*/React__default.createElement(StyledThumb, {
    onClick: function onClick() {
      if (thumbClickHandler !== undefined) {
        var isGameOpened = !showTimer();
        return thumbClickHandler(thumb, game, isGameOpened);
      } else {
        onThumbClick(game);
      }
    },
    closed: isGameClosed()
  }, /*#__PURE__*/React__default.createElement("img", {
    src: thumb.bg,
    alt: ""
  }), /*#__PURE__*/React__default.createElement(FlagBar, {
    showFlag: thumb.showFlag,
    providerName: thumb.providerName
  }), thumb.title && /*#__PURE__*/React__default.createElement(TitleBox, {
    titlePosition: thumb.title.titlePosition
  }, /*#__PURE__*/React__default.createElement("h4", null, thumb.title.topTitle), /*#__PURE__*/React__default.createElement("h3", null, thumb.title.middleTitle), /*#__PURE__*/React__default.createElement(TitleDesc, null, !thumb.gameId ? "- " + providerGamesCount(thumb.providerKey) + " \xE5bne borde -" : thumb.title.bottomTitle)), showTimer() ? /*#__PURE__*/React__default.createElement(TimerWrapper, {
    left: '28%'
  }, /*#__PURE__*/React__default.createElement(CountdownTimer, {
    startTime: convertTimeWithZone((_thumb$liveCasino$wor = thumb.liveCasino.workHours.value) === null || _thumb$liveCasino$wor === void 0 ? void 0 : _thumb$liveCasino$wor.startTime),
    completeCallback: function completeCallback(status) {
      return setFinishedCountdown(status);
    },
    onMountCallback: function onMountCallback(status) {
      return setFinishedCountdown(status);
    }
  }), /*#__PURE__*/React__default.createElement(TimeRange, null, convertTimeWithZone((_thumb$liveCasino$wor2 = thumb.liveCasino.workHours.value) === null || _thumb$liveCasino$wor2 === void 0 ? void 0 : _thumb$liveCasino$wor2.startTime) + " -\n\n                                                " + convertTimeWithZone((_thumb$liveCasino$wor3 = thumb.liveCasino.workHours.value) === null || _thumb$liveCasino$wor3 === void 0 ? void 0 : _thumb$liveCasino$wor3.endTime))) : null, /*#__PURE__*/React__default.createElement(BottomBar, {
    liveCasinoData: thumb.liveCasino,
    showPlayersCount: thumb.players
  })));
};

ProvidersThumb.propTypes = {
  liveCasinoGames: PropTypes.arrayOf(PropTypes.object).isRequired,
  thumb: PropTypes.object.isRequired,
  thumbClickHandler: PropTypes.func
};

var ProvidersThumbs = function ProvidersThumbs(_ref) {
  var activeCategoryName = _ref.activeCategoryName,
      thumbs = _ref.thumbs,
      showProviders = _ref.showProviders,
      thumbClickHandler = _ref.thumbClickHandler,
      liveCasinoGames = _ref.liveCasinoGames;

  var _useState = useState(thumbs),
      thumbsToDisplay = _useState[0],
      setThumbsToDisplay = _useState[1];

  var isToShow = function isToShow() {
    return ['all'].indexOf(activeCategoryName) >= 0 && liveCasinoGames.length;
  };

  var providerGamesCount = function providerGamesCount(provider) {
    if (provider === void 0) {
      provider = '';
    }

    return liveCasinoGames && liveCasinoGames.length ? liveCasinoGames.filter(function (game) {
      return game.subprovider === provider;
    }).length : 0;
  };

  var showByProvider = function showByProvider(provider) {
    if (provider === void 0) {
      provider = '';
    }

    return showProviders.length === 0 ? true : showProviders.indexOf(provider) >= 0;
  };

  useEffect(function () {
    setThumbsToDisplay(thumbs.filter(function (item) {
      return showByProvider(item.providerKey) && providerGamesCount(item.providerKey);
    }));
  }, [thumbs]);
  return /*#__PURE__*/React__default.createElement(Fragment, null, isToShow() ? thumbsToDisplay.map(function (thumb, key) {
    if (Object.keys(thumb.liveCasino).length && thumb.gameId || !Object.keys(thumb.liveCasino).length && !thumb.gameId) {
      return /*#__PURE__*/React__default.createElement(ProvidersThumb, {
        key: key,
        thumb: thumb,
        thumbClickHandler: thumbClickHandler,
        liveCasinoGames: liveCasinoGames
      });
    }
  }) : null);
};

ProvidersThumbs.propTypes = {
  activeCategoryName: PropTypes.string,
  thumb: PropTypes.object,
  thumbClickHandler: PropTypes.func
};

var _defs, _g$4;

function _extends$a() {
  _extends$a = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$a.apply(this, arguments);
}

function SvgLock(props) {
  return /*#__PURE__*/createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 32,
    height: 32
  }, props), _defs || (_defs = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "lock_svg__a"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Rectangle 3295",
    transform: "translate(1521 1601)",
    fill: "#fff",
    d: "M0 0h32v32H0z"
  })))), _g$4 || (_g$4 = /*#__PURE__*/createElement("g", {
    "data-name": "Mask Group 17",
    transform: "translate(-1521 -1601)",
    clipPath: "url(#lock_svg__a)"
  }, /*#__PURE__*/createElement("g", {
    "data-name": "Group 3331"
  }, /*#__PURE__*/createElement("path", {
    "data-name": "Path 8914",
    d: "M1527.473 1610.612v3.341h-.222a2.539 2.539 0 00-2.537 2.537v13.973a2.539 2.539 0 002.537 2.537h19.5a2.539 2.539 0 002.537-2.537v-13.98a2.539 2.539 0 00-2.537-2.537h-.065v-3.341a9.605 9.605 0 10-19.21.007zm11.027 13.482a.542.542 0 00-.248.51c.007 1.027.007 2.047.007 3.073a1.094 1.094 0 01-.575 1.033 1.2 1.2 0 01-1.792-1.033v-.007c0-1.027 0-2.053.007-3.08a.523.523 0 00-.229-.484 2.341 2.341 0 01-.772-2.884 2.389 2.389 0 014.57.942 2.269 2.269 0 01-.968 1.93zm-6.822-13.482a5.4 5.4 0 0110.8 0v3.341h-10.8z",
    fill: "#fff"
  })))));
}

var imageSizes = {
  xs: '_xs',
  sm: '_sm',
  md: '_md',
  lg: '_lg',
  xl: '_xl',
  xxl: '_xxl'
};
var imagePath = CDN_IMG_PATH;

var replaceImagePath = function replaceImagePath(imagePath, imageSize) {
  if (imageSize === void 0) {
    imageSize = 'xs';
  }

  var splitPath = imagePath.split('/');
  var image = splitPath[splitPath.length - 1];
  var imageName = image.split('.')[0];
  splitPath[splitPath.length - 1] = image.replace(imageName, imageName + imageSizes[imageSize]);
  return splitPath.join('/');
};

var checkImage = function checkImage(url, cb) {
  new Promise(function (resolve, reject) {
    var img = new Image();
    img.src = url;

    img.onload = function () {
      return resolve(url);
    };

    img.onerror = function () {
      reject();
    };
  }).then(function (validUrl) {
    cb(validUrl);
  })["catch"](function () {
    cb(false);
  });
};

var imageSvg = function imageSvg(game, cb) {
  var _game$images, _game$images$svg, _game$svgimage;

  if (game !== null && game !== void 0 && (_game$images = game.images) !== null && _game$images !== void 0 && (_game$images$svg = _game$images.svg) !== null && _game$images$svg !== void 0 && _game$images$svg.length && game.alt && parseFloat(game.alt)) {
    var svgImg = "" + CMS_PATH + game.images.svg;
    checkImage(svgImg, function (validUrl) {
      return cb(validUrl);
    });
  } else if (game !== null && game !== void 0 && (_game$svgimage = game.svgimage) !== null && _game$svgimage !== void 0 && _game$svgimage.length && game.alt && parseFloat(game.alt)) {
    var _svgImg = "" + CMS_PATH + game.svgimage;

    checkImage(_svgImg, function (validUrl) {
      return cb(validUrl);
    });
  } else {
    cb(false);
  }
};
var defaultImage = function defaultImage(game, size, cb) {
  var _game$images2, _game$images2$thumb, _game$image;

  if (game !== null && game !== void 0 && (_game$images2 = game.images) !== null && _game$images2 !== void 0 && (_game$images2$thumb = _game$images2.thumb) !== null && _game$images2$thumb !== void 0 && _game$images2$thumb.length) {
    var imgSrc = "" + CMS_PATH + replaceImagePath(game.images.thumb, size);
    checkImage(imgSrc, function (validUrl) {
      return cb(validUrl);
    });
  } else if (game !== null && game !== void 0 && (_game$image = game.image) !== null && _game$image !== void 0 && _game$image.length) {
    var _imgSrc = "" + CMS_PATH + replaceImagePath(game.image, size);

    checkImage(_imgSrc, function (validUrl) {
      return cb(validUrl);
    });
  } else {
    cb(false);
  }
};
var reserveImage = function reserveImage(gameId) {
  return gameId ? imagePath + gameId + '-tmb.jpg' : '';
};
var getImageSrc = function getImageSrc(game, cb) {
  imageSvg(game, function (imageUrl) {
    if (imageUrl) {
      cb(imageUrl);
    } else {
      defaultImage(game, 'md', function (imageUrl) {
        cb(imageUrl || reserveImage(game.game_id));
      });
    }
  });
};

var _templateObject$8, _templateObject2$7, _templateObject3$5, _templateObject4$5, _templateObject5$2, _templateObject6$2;
var skeletonLoader = IMG_PATH + 'skeleton-loader.gif';
var GameThumbWrapper = styled.div(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteralLoose(["\n    position: relative;\n    transition: all 0.3s ease;\n    overflow: hidden;\n    border-radius: 8px;\n    border: 2px solid\n        ", ";\n\n    :hover {\n        cursor: ", ";\n        transform: scale(1.05);\n    }\n"])), function (props) {
  return props.privateTable ? '#C49B53' : 'transparent';
}, function (props) {
  return props.closed || props.locked ? 'default' : 'pointer';
});
var TimerText = styled.p(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteralLoose(["\n    color: #fff;\n    font-size: 12px;\n    line-height: 12px;\n    text-align: center;\n    margin: 0 0 8px;\n"])));
var GameThumb = styled.img(_templateObject3$5 || (_templateObject3$5 = _taggedTemplateLiteralLoose(["\n    height: 100%;\n    min-height: 175px;\n    width: 100%;\n    object-fit: cover;\n\n    @media (max-width: ", ") {\n        height: 160px;\n        min-height: auto;\n    }\n\n    @media (max-width: ", ") {\n        height: 140px;\n        min-height: auto;\n    }\n\n    @media (max-width: ", ") {\n        height: 130px;\n        min-height: auto;\n    }\n\n    @media (max-width: ", ") {\n        height: 120px;\n        min-height: auto;\n    }\n\n    ", ";\n"])), BREAKPOINTS.xl + 'px', BREAKPOINTS.lg + 'px', BREAKPOINTS.md + 'px', BREAKPOINTS.sm + 'px', function (props) {
  return props.closed && css(_templateObject4$5 || (_templateObject4$5 = _taggedTemplateLiteralLoose(["\n            filter: brightness(50%);\n        "])));
});
var LockedGame = styled.div(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    text-align: center;\n    position: absolute;\n    width: 50px;\n    min-height: 50px;\n    top: 50%;\n    left: 50%;\n    transform: translate(-50%, -50%);\n    z-index: 1;\n"])));
var LockGameText = styled.div(_templateObject6$2 || (_templateObject6$2 = _taggedTemplateLiteralLoose(["\n    color: #fff;\n    font-size: 14px;\n    opacity: 70%;\n    margin: 15px 0 0;\n"])));

var GameCard = function GameCard(_ref) {
  var _liveCasinoGame$workH, _liveCasinoGame$seats, _game$liveCas$workHou, _game$liveCas$workHou2;

  var game = _ref.game,
      _ref$lazyOverflow = _ref.lazyOverflow,
      lazyOverflow = _ref$lazyOverflow === void 0 ? false : _ref$lazyOverflow,
      width = _ref.width,
      height = _ref.height,
      gameClickHandler = _ref.gameClickHandler;
  var liveCasinoGame = game.liveCas;

  var _useState = useState(''),
      imgPath = _useState[0],
      setImgPath = _useState[1];

  var _useState2 = useState(false),
      isActive = _useState2[0],
      setIsActive = _useState2[1];

  var _useState3 = useState(false),
      finishedCountdown = _useState3[0],
      setFinishedCountdown = _useState3[1];

  var privateTable = function privateTable() {
    return typeof liveCasinoGame.privateTableConfig === 'object' && liveCasinoGame.privateTableConfig !== null ? liveCasinoGame.privateTableConfig.isPrivate : false;
  };

  var isGameClosed = !(liveCasinoGame !== null && liveCasinoGame !== void 0 && liveCasinoGame.open) && isObject(liveCasinoGame === null || liveCasinoGame === void 0 ? void 0 : (_liveCasinoGame$workH = liveCasinoGame.workHours) === null || _liveCasinoGame$workH === void 0 ? void 0 : _liveCasinoGame$workH.value) && (liveCasinoGame === null || liveCasinoGame === void 0 ? void 0 : liveCasinoGame.operationHours) === OPEN_HOURS_TYPE && !finishedCountdown;
  var isGameLocked = (liveCasinoGame === null || liveCasinoGame === void 0 ? void 0 : liveCasinoGame.seatsTaken) !== null && (liveCasinoGame === null || liveCasinoGame === void 0 ? void 0 : (_liveCasinoGame$seats = liveCasinoGame.seatsTaken) === null || _liveCasinoGame$seats === void 0 ? void 0 : _liveCasinoGame$seats.length) >= 1 && privateTable();

  var setLiveImg = function setLiveImg() {
    getImageSrc(game, function (imgPath) {
      var _liveCasinoGame$thumb;

      var imgSize = 'M';
      var findImgPath = (((liveCasinoGame || {}).thumbs || {}).thumbnails || {})[imgSize] ? (liveCasinoGame === null || liveCasinoGame === void 0 ? void 0 : (_liveCasinoGame$thumb = liveCasinoGame.thumbs) === null || _liveCasinoGame$thumb === void 0 ? void 0 : _liveCasinoGame$thumb.thumbnails[imgSize]) + '?t=' + new Date().getTime() : imgPath;
      return setImgPath(findImgPath);
    });
  };

  var appContextData = useContext(AppContext);

  var onGameClick = function onGameClick(game) {
    var userInfo = appContextData.userInfo;

    if (userInfo && userInfo.userId) {
      if (userInfo.status === 'open' || userInfo.status === 'temp') {
        GameLaunchService.LaunchGame(game, userInfo, appContextData.skin);
      } else {
        appContextData.notification({
          text: 'User is selfExcluded',
          type: 'selfExcluded'
        });
      }
    } else {
      appContextData.notification({
        text: 'Du spiller nu for sjov. Opret en konto eller log ind for at spille for rigtige penge',
        type: 'warning'
      });
      return;
    }
  };

  useEffect(function () {
    setLiveImg();
    var interval = setInterval(setLiveImg, 1000 * 15);
    setTimeout(function () {
      return clearInterval(interval);
    }, 1000 * 60 * 15);
    return function () {
      return clearInterval(interval);
    };
  }, []);
  return /*#__PURE__*/React__default.createElement(LazyLoad, {
    once: true,
    placeholder: /*#__PURE__*/React__default.createElement("img", {
      height: 'auto',
      width: '100%',
      src: skeletonLoader,
      alt: "skeletonImg",
      style: {
        borderRadius: 8,
        maxHeight: '176px'
      }
    }),
    overflow: lazyOverflow
  }, /*#__PURE__*/React__default.createElement(GameThumbWrapper, {
    closed: isGameClosed || isGameLocked,
    onMouseEnter: function onMouseEnter() {
      return setIsActive(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsActive(false);
    },
    privateTable: privateTable(),
    onClick: function onClick() {
      if (!isGameClosed && !isGameLocked) {
        if (gameClickHandler !== undefined) {
          return gameClickHandler(game, !isGameClosed);
        } else {
          onGameClick(game);
        }
      }
    }
  }, isGameClosed && /*#__PURE__*/React__default.createElement(TimerWrapper, {
    top: '45%'
  }, /*#__PURE__*/React__default.createElement(TimerText, null, "\xC5bner om"), /*#__PURE__*/React__default.createElement(CountdownTimer, {
    startTime: convertTimeWithZone((_game$liveCas$workHou = game.liveCas.workHours) === null || _game$liveCas$workHou === void 0 ? void 0 : (_game$liveCas$workHou2 = _game$liveCas$workHou.value) === null || _game$liveCas$workHou2 === void 0 ? void 0 : _game$liveCas$workHou2.startTime),
    completeCallback: function completeCallback(status) {
      return setFinishedCountdown(status);
    },
    onMountCallback: function onMountCallback(status) {
      return setFinishedCountdown(status);
    }
  })), /*#__PURE__*/React__default.createElement(FlagBar, {
    providerName: game.subprovider
  }), isGameLocked && /*#__PURE__*/React__default.createElement(LockedGame, null, /*#__PURE__*/React__default.createElement(SvgLock, null), /*#__PURE__*/React__default.createElement(LockGameText, null, "Optaget")), /*#__PURE__*/React__default.createElement(GameThumb, {
    width: width,
    height: height,
    src: imgPath ? imgPath : skeletonLoader,
    onError: function onError() {
      return setImgPath(skeletonLoader);
    },
    loading: "lazy",
    closed: isGameClosed || isGameLocked
  }), /*#__PURE__*/React__default.createElement(BottomBar, {
    liveCasinoData: game.liveCas,
    showPlayersCount: !!Object.keys(game.liveCas).length && !privateTable(),
    subprovider: game.subprovider,
    active: isActive,
    gameType: game.subcat,
    privateTable: privateTable()
  })));
};

GameCard.propTypes = {
  game: PropTypes.object.isRequired,
  lazyOverflow: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  gameClickHandler: PropTypes.func
};

var _templateObject$9, _templateObject2$8, _templateObject3$6, _templateObject4$6, _templateObject5$3;
var MAX_NUMBER_OF_GAMES = 200;
var GameHolder = styled.div(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteralLoose(["\n    position: relative;\n    padding: ", ";\n    ", ";\n    &:hover {\n        cursor: pointer;\n    }\n"])), function (props) {
  return props.itemPadding ? props.itemPadding : 'initial';
}, function (props) {
  return props.thumb && css(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteralLoose(["\n            grid-column: ", ";\n            grid-row: 1/3;\n        "])), function (props) {
    return props.index % 2 ? '3/5' : '1/3';
  });
});
var ThumbWrapper = styled.div(_templateObject3$6 || (_templateObject3$6 = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    justify-content: center;\n    transition: all 0.3s ease;\n    width: ", ";\n    position: relative;\n    line-height: 0; //removing space between picture and border\n"])), function (_ref) {
  var width = _ref.width;
  return width ? width : 'auto';
});
var GameText = styled(Text)(_templateObject4$6 || (_templateObject4$6 = _taggedTemplateLiteralLoose(["\n    text-align: center;\n    position: relative;\n    top: 5px;\n    color: ", ";\n    margin: 0;\n    line-height: 1.3;\n    font-size: 16px;\n"])), UI_CONFIG.textColor);
var MinStake = styled(HighlightBox)(_templateObject5$3 || (_templateObject5$3 = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    background: rgba(0, 0, 0, 0.8);\n    color: white;\n    padding: 0 5px;\n    font-size: 0.7rem;\n    border-radius: 4px;\n    position: absolute;\n    bottom: 13px;\n    right: 7px;\n"])));

var GameList = function GameList(_ref2) {
  var _ref2$games = _ref2.games,
      games = _ref2$games === void 0 ? [] : _ref2$games,
      _ref2$limit = _ref2.limit,
      limit = _ref2$limit === void 0 ? MAX_NUMBER_OF_GAMES : _ref2$limit,
      _ref2$itemPadding = _ref2.itemPadding,
      itemPadding = _ref2$itemPadding === void 0 ? 'initial' : _ref2$itemPadding,
      _ref2$lazyOverflow = _ref2.lazyOverflow,
      lazyOverflow = _ref2$lazyOverflow === void 0 ? false : _ref2$lazyOverflow,
      gameClickHandler = _ref2.gameClickHandler;

  var _useState = useState([]),
      gamesForRender = _useState[0],
      setGamesForRender = _useState[1];

  var _useState2 = useState(0),
      keyUpdate = _useState2[0];

  useEffect(function () {
    setGamesForRender(games);
  }, [games]);
  return /*#__PURE__*/React__default.createElement(Fragment, {
    key: keyUpdate
  }, gamesForRender.length > 0 && gamesForRender.map(function (game, index) {
    return index < limit && /*#__PURE__*/React__default.createElement(GameHolder, {
      key: 'gamesList-' + index,
      itemPadding: itemPadding,
      index: index
    }, /*#__PURE__*/React__default.createElement(ThumbWrapper, {
      width: '100%'
    }, /*#__PURE__*/React__default.createElement(GameCard, {
      key: game.id,
      game: game,
      gameClickHandler: gameClickHandler,
      lazyOverflow: lazyOverflow
    })), /*#__PURE__*/React__default.createElement(GameText, null, game.name));
  }));
};

GameList.propTypes = {
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  limit: PropTypes.number,
  lazyOverflow: PropTypes.bool,
  itemPadding: PropTypes.string,
  gameClickHandler: PropTypes.func
};

var _templateObject$a, _templateObject2$9, _templateObject3$7, _templateObject4$7;
var ContentWrapper = styled.div(_templateObject$a || (_templateObject$a = _taggedTemplateLiteralLoose(["\n    width: 100%;\n    height: auto;\n    min-height: auto;\n    background: #f8f8fa;\n    overflow-y: unset;\n"])));
var ProvidersThumbBlock = styled(CenterBlock)(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteralLoose(["\n    display: flex;\n    flex-direction: row;\n    flex-wrap: wrap;\n    justify-content: space-between;\n"])));
var StyledThumbBlock = styled(CenterBlock)(_templateObject3$7 || (_templateObject3$7 = _taggedTemplateLiteralLoose(["\n    margin-top: 50px;\n    margin-bottom: 50px;\n    padding: 0 25px;\n    min-height: 300px;\n\n    @media (max-width: ", ") {\n        font-size: 12px;\n        line-height: 12px;\n    }\n"])), BREAKPOINTS.lg + 'px');
var MediaGridCustom = styled(MediaGrid)(_templateObject4$7 || (_templateObject4$7 = _taggedTemplateLiteralLoose(["\n    @media (max-width: ", ") {\n        grid-template-columns: repeat(2, 1fr);\n    }\n\n    @media (min-width: ", ") {\n        grid-template-columns: repeat(4, 1fr);\n    }\n"])), BREAKPOINTS.sm + 'px', BREAKPOINTS.md + 1 + 'px');

var Content = function Content(_ref) {
  var activeCategory = _ref.activeCategory,
      liveCasinoGames = _ref.liveCasinoGames,
      showThumbsByProviders = _ref.showThumbsByProviders,
      showGamesByProviders = _ref.showGamesByProviders,
      thumbClickHandler = _ref.thumbClickHandler,
      gameClickHandler = _ref.gameClickHandler,
      _ref$gamesCount = _ref.gamesCount,
      gamesCount = _ref$gamesCount === void 0 ? function (f) {
    return f;
  } : _ref$gamesCount;

  var _useState = useState([]),
      displayedGames = _useState[0],
      setDisplayedGames = _useState[1];

  var _useState2 = useState(0),
      setCategoryCount = _useState2[1];

  var _useState3 = useState([]),
      providersThumbs = _useState3[0],
      setProvidersThumbs = _useState3[1];

  var findTableForProviderThumb = function findTableForProviderThumb(gameId) {
    var matchGame = liveCasinoGames.find(function (game) {
      return game.id === gameId;
    });
    return matchGame ? matchGame.liveCas : {};
  };

  var matchProvidersThumbToTable = function matchProvidersThumbToTable() {
    return PROVIDERS_THUMBS.map(function (thumb) {
      return _extends({}, thumb, {
        liveCasino: thumb.gameId ? findTableForProviderThumb(thumb.gameId) : {}
      });
    });
  };

  var showByProvider = function showByProvider(provider) {
    if (provider === void 0) {
      provider = '';
    }

    return showGamesByProviders.length === 0 ? true : showGamesByProviders.indexOf(provider) >= 0;
  };

  var filterByOpened = function filterByOpened(game) {
    return game.liveCas;
  };

  var filterGamesByCategory = function filterGamesByCategory(games, categoryName) {
    return games.filter(function (game) {
      return game.liveCasinoPageCategories.includes(categoryName) && showByProvider(game.subprovider) && filterByOpened(game);
    });
  };

  var sortingGames = function sortingGames(games) {
    return games.sort(function (a, b) {
      return Number(b.tag_hot) - Number(a.tag_hot) || parseFloat(b.clicks || 0) - parseFloat(a.clicks || 0) || a.name.localeCompare(b.name);
    }).sort(function (a, b) {
      return b.tag_hot && a.tag_hot ? parseInt(a.priority) - parseInt(b.priority) : 0;
    });
  };

  useEffect(function () {
    var filteredGames = filterGamesByCategory(liveCasinoGames, activeCategory.name);
    setCategoryCount(filteredGames.length);
    gamesCount(filteredGames.length);
    setDisplayedGames(sortingGames(filteredGames));
  }, [activeCategory, liveCasinoGames]);
  useEffect(function () {
    setProvidersThumbs(matchProvidersThumbToTable());
  }, []);
  return /*#__PURE__*/React__default.createElement(ContentWrapper, null, /*#__PURE__*/React__default.createElement(ProvidersThumbBlock, null, providersThumbs && /*#__PURE__*/React__default.createElement(ProvidersThumbs, {
    activeCategoryName: activeCategory.name,
    thumbs: providersThumbs,
    showProviders: showThumbsByProviders,
    thumbClickHandler: thumbClickHandler,
    liveCasinoGames: liveCasinoGames
  })), /*#__PURE__*/React__default.createElement(StyledThumbBlock, null, displayedGames.length ? /*#__PURE__*/React__default.createElement(MediaGridCustom, null, /*#__PURE__*/React__default.createElement(GameList, {
    games: displayedGames,
    gameClickHandler: gameClickHandler
  })) : /*#__PURE__*/React__default.createElement(Text, {
    align: 'center',
    pt: 3
  }, "Ingen match fundet.")));
};

Content.propTypes = {
  activeCategory: PropTypes.object.isRequired,
  liveCasinoGames: PropTypes.arrayOf(PropTypes.object).isRequired,
  showThumbsByProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
  showGamesByProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
  thumbClickHandler: PropTypes.func,
  gameClickHandler: PropTypes.func,
  gamesCount: PropTypes.func
};

var _templateObject$b, _templateObject2$a, _templateObject3$8, _templateObject4$8, _templateObject5$4;
var CATEGORY_IMG_PATH = IMG_PATH + 'categories/';
var BannerWrapper = styled.div(_templateObject$b || (_templateObject$b = _taggedTemplateLiteralLoose(["\n    position: relative;\n    width: 100%;\n    min-height: 250px;\n    height: 35vw;\n    overflow: hidden;\n    max-height: 460px;\n\n    background: ", ";\n\n    @media (max-width: ", ") {\n        min-height: 150px;\n    }\n"])), function (props) {
  return props.bgImage ? "url(" + props.bgImage + ") top/cover no-repeat" : "transparent linear-gradient(104deg, " + props.bgGradient.leftColor + " 0%, " + props.bgGradient.rightColor + " 100%) 0% 0% no-repeat padding-box";
}, BREAKPOINTS.sm + 'px');
var BannerDesc = styled.div(_templateObject2$a || (_templateObject2$a = _taggedTemplateLiteralLoose(["\n    margin: 50px 0 0 120px;\n    float: left;\n    transition: all 0.3s ease 0s;\n\n    @media (max-width: ", ") {\n        margin: 10px 0 0 30px;\n    }\n\n    @media (max-width: ", ") {\n        margin: 20px 0 0 30px;\n    }\n\n    svg {\n        height: 130px;\n\n        @media (max-width: ", ") {\n            height: 50px;\n        }\n    }\n"])), BREAKPOINTS.md + 'px', BREAKPOINTS.sm + 'px', BREAKPOINTS.sm + 'px');
var BannerTitle = styled.h2(_templateObject3$8 || (_templateObject3$8 = _taggedTemplateLiteralLoose(["\n    color: #fff;\n    font-size: 66px;\n    margin: 15px 0;\n\n    @media (max-width: ", ") {\n        font-size: 48px;\n        margin: 5px 0;\n    }\n\n    @media (max-width: ", ") {\n        font-size: 36px;\n        margin: 5px 0;\n    }\n\n    @media (max-width: ", ") {\n        font-size: 22px;\n        margin: 10px 0;\n    }\n"])), BREAKPOINTS.lg + 'px', BREAKPOINTS.md + 'px', BREAKPOINTS.sm + 'px');
var BannerCount = styled.p(_templateObject4$8 || (_templateObject4$8 = _taggedTemplateLiteralLoose(["\n    color: rgba(255, 255, 255, 0.5);\n    font-size: 42px;\n    margin: 0;\n    transition: all 0.3s ease 0s;\n\n    @media (max-width: ", ") {\n        font-size: 26px;\n    }\n\n    @media (max-width: ", ") {\n        font-size: 22px;\n    }\n\n    @media (max-width: ", ") {\n        font-size: 14px;\n    }\n"])), BREAKPOINTS.lg + 'px', BREAKPOINTS.md + 'px', BREAKPOINTS.sm + 'px');
var BannerImg = styled.img(_templateObject5$4 || (_templateObject5$4 = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    bottom: 0;\n    right: 0;\n    height: 95%;\n    transition: all 0.3s ease 0s;\n\n    @media (max-width: ", ") {\n        height: 90%;\n    }\n"])), BREAKPOINTS.sm + 'px');

var Banner = function Banner(_ref) {
  var activeCategory = _ref.activeCategory,
      _ref$gamesCount = _ref.gamesCount,
      gamesCount = _ref$gamesCount === void 0 ? 0 : _ref$gamesCount;

  var _useState = useState({}),
      setCategory = _useState[1];

  useEffect(function () {
    setCategory(activeCategory);
  }, [activeCategory]);
  return /*#__PURE__*/React__default.createElement(Fragment, null, Object.keys(activeCategory).length && /*#__PURE__*/React__default.createElement(BannerWrapper, {
    bgImage: activeCategory.bgImage ? IMG_PATH + activeCategory.bgImage : '',
    bgGradient: Object.keys(activeCategory.bgGradient).length ? activeCategory.bgGradient : ''
  }, activeCategory.name !== 'all' && /*#__PURE__*/React__default.createElement(CenterBlock, {
    style: {
      height: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(BannerDesc, null, activeCategory.icon, /*#__PURE__*/React__default.createElement(BannerTitle, null, activeCategory.label), /*#__PURE__*/React__default.createElement(BannerCount, null, gamesCount ? gamesCount : 'XX', " borde")), /*#__PURE__*/React__default.createElement(BannerImg, {
    src: CATEGORY_IMG_PATH + activeCategory.person,
    alt: ''
  }))));
};

Banner.propTypes = {
  activeCategory: PropTypes.object.isRequired,
  gamesCount: PropTypes.number
};

var GameDTO = /*#__PURE__*/function () {
  function GameDTO(game) {

    this.jackpot_amount = '';
  }

  GameDTO.unserialize = function unserialize(game) {
    return _extends({}, game, new GameDTO(game));
  };

  GameDTO.serialize = function serialize(game) {
    return {};
  };

  return GameDTO;
}();

var gameMapper = function gameMapper(game) {
  if (game.name) return GameDTO.unserialize(game);
};

var getGamesByKey = function getGamesByKey(allGames) {
  var games = allGames.map(function (el) {
    return gameMapper(el);
  });
  return games;
};

var casinoMapper = function casinoMapper(games) {
  var mappedGames = games;
  mappedGames.forEach(function (game) {
    if (game.rod_hide === false) {
      game['liveCasinoPageCategories'] = [];
      if (game.category === 'liveint' || game.category === 'livedk') game['liveCasinoPageCategories'].push('all');
      if ((game.category === 'liveint' || game.category === 'livedk') && game.subcat === 'roulette') game['liveCasinoPageCategories'].push('roulette');
      if (game.category === 'liveint' && game.subcat === 'blackjack') game['liveCasinoPageCategories'].push('blackJack');
      if (game.category === 'liveint' && game.subcat === 'baccarat') game['liveCasinoPageCategories'].push('baccarat');
      if (game.category === 'liveint' && game.subcat === 'poker') game['liveCasinoPageCategories'].push('poker');
    }
  });
  return mappedGames;
};

var getLiveTablesAdapter = function getLiveTablesAdapter() {
  return get(MAIN_REST_BASE_API, {
    r: 'GetLiveTables'
  }).then(function (res) {
    if (res && res.data) {
      return res.data;
    }
  });
};
var getLiveCasinoGamesAdapter = function getLiveCasinoGamesAdapter() {
  return get(API_URL + 'categories/liveint/games', {
    platform: getPlatformForApi(),
    srv: ENV(),
    app: APP_NAME,
    'page[size]': 500
  }).then(function (res) {
    var mappedGames = casinoMapper(getGamesByKey(res.data));
    return mappedGames;
  });
};
var getAllGames = function getAllGames(mappedGames, liveTableGames) {
  var newArr = [];
  mappedGames.forEach(function (game) {
    if (game.provider === 'evolution') {
      Object.entries(liveTableGames).forEach(function (_ref) {
        var tableID = _ref[0],
            liveTableGame = _ref[1];

        if (liveTableGame.open || !liveTableGame.open && liveTableGame.operationHours === OPEN_HOURS_TYPE) {
          if (tableID === game.table_id) {
            var newEl = _extends({}, game, {
              liveCas: _extends({}, liveTableGame)
            });

            newArr.push(newEl);
          }
        }
      });
    } else {
      newArr.push(_extends({}, game, {
        liveCas: {}
      }));
    }
  });
  return {
    evolutionGamesState: mappedGames,
    liveCasinoGames: newArr
  };
};

var version = "1.3.20";

var _templateObject$c, _templateObject2$b, _templateObject3$9;
var bounceDelay = keyframes(_templateObject$c || (_templateObject$c = _taggedTemplateLiteralLoose(["\n  0%, 80%, 100% {\n    -webkit-transform: scale(0);\n    transform: scale(0);\n  } 40% {\n    -webkit-transform: scale(1.0);\n    transform: scale(1.0);\n  }\n"])));
var Bounce = styled.div(_templateObject2$b || (_templateObject2$b = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    width: 12px;\n    height: 12px;\n    margin: 0 3px;\n    background: ", ";\n\n    border-radius: 100%;\n    -webkit-animation: ", " 1.4s infinite ease-in-out both;\n    animation: ", " 1.4s infinite ease-in-out both;\n"])), function (props) {
  return props.background ? props.background : UI_CONFIG.loaderColor;
}, bounceDelay, bounceDelay);
var Wrapper = styled.div(_templateObject3$9 || (_templateObject3$9 = _taggedTemplateLiteralLoose(["\n    display: inline-block;\n    text-align: center;\n    & > :first-child {\n        -webkit-animation-delay: -0.32s;\n        animation-delay: -0.32s;\n    }\n    & > :nth-child(2) {\n        -webkit-animation-delay: -0.16s;\n        animation-delay: -0.16s;\n    }\n"])));

var Loader = function Loader(_ref) {
  var _ref$color = _ref.color,
      color = _ref$color === void 0 ? UI_CONFIG.loaderColor : _ref$color;
  return /*#__PURE__*/React__default.createElement(Wrapper, null, /*#__PURE__*/React__default.createElement(Bounce, {
    background: color
  }), /*#__PURE__*/React__default.createElement(Bounce, {
    background: color
  }), /*#__PURE__*/React__default.createElement(Bounce, {
    background: color
  }));
};

var _templateObject$d, _templateObject2$c, _templateObject3$a;
var PageWrapper = styled.div(_templateObject$d || (_templateObject$d = _taggedTemplateLiteralLoose(["\n    display: flex;\n    flex-direction: column;\n    min-height: inherit;\n    overflow-x: hidden;\n    overflow-y: auto;\n"])));
var BannerContainer = styled.div(_templateObject2$c || (_templateObject2$c = _taggedTemplateLiteralLoose(["\n    display: flex;\n    width: 100%;\n    position: relative;\n    min-height: 200px;\n\n    @media (max-width: ", ") {\n        min-height: initial;\n    }\n"])), BREAKPOINTS.sm + 'px');
var MenuContainer = styled.div(_templateObject3$a || (_templateObject3$a = _taggedTemplateLiteralLoose(["\n    display: flex;\n    width: 100%;\n    height: 80px;\n    background: #fff;\n    justify-content: center;\n    flex-direction: column;\n"])));

var LiveCasino = function LiveCasino(_ref) {
  var _ref$appName = _ref.appName,
      appName = _ref$appName === void 0 ? 'rod25' : _ref$appName,
      _ref$liveCasinoGames = _ref.liveCasinoGames,
      liveCasinoGames = _ref$liveCasinoGames === void 0 ? null : _ref$liveCasinoGames,
      matchUrl = _ref.matchUrl,
      urlParams = _ref.urlParams,
      _ref$showThumbsByProv = _ref.showThumbsByProviders,
      showThumbsByProviders = _ref$showThumbsByProv === void 0 ? [] : _ref$showThumbsByProv,
      _ref$showGamesByProvi = _ref.showGamesByProviders,
      showGamesByProviders = _ref$showGamesByProvi === void 0 ? [] : _ref$showGamesByProvi,
      _ref$menuColors = _ref.menuColors,
      menuColors = _ref$menuColors === void 0 ? {} : _ref$menuColors,
      thumbClickHandler = _ref.thumbClickHandler,
      gameClickHandler = _ref.gameClickHandler,
      userInfo = _ref.userInfo,
      notificationHandler = _ref.notificationHandler;

  var basicMenuColors = _extends({}, UI_CONFIG.menuColors, menuColors);

  var _useState = useState({}),
      activeCategory = _useState[0],
      setActiveCategory = _useState[1];

  var _useState2 = useState(0),
      gamesCount = _useState2[0],
      setGamesCount = _useState2[1];

  var _useState3 = useState([]),
      liveCasino = _useState3[0],
      setLiveCasino = _useState3[1];

  var _useState4 = useState(true),
      loader = _useState4[0],
      setLoader = _useState4[1];

  var appContext = {
    skin: getEvoSkin(appName),
    userInfo: userInfo,
    notification: notificationHandler
  };

  var setUrlByCategory = function setUrlByCategory(category) {
    matchUrl(category);
  };

  var onChangeTabHandler = function onChangeTabHandler(tab) {
    setUrlByCategory(tab.url);
  };

  var matchCategory = function matchCategory() {
    return LIVE_CASINO_CATEGORIES.find(function (c) {
      return c.url === urlParams.category;
    });
  };

  var getLiveTables = function getLiveTables(casinoGames) {
    getLiveTablesAdapter().then(function (res) {
      if (casinoGames && res) {
        setLiveCasino(getAllGames(casinoGames, res).liveCasinoGames);
        setTimeout(function () {
          return setLoader(false);
        }, 1000);
      }
    })["catch"](function (e) {
      setLiveCasino([]);
      appContext.notification({
        text: 'Der er opstået et problem, prøv venligst senere',
        type: 'error'
      });
    });
  };

  useEffect(function () {
    var interval;

    if (liveCasinoGames !== null) {
      setLiveCasino(liveCasinoGames);

      if (liveCasinoGames.length) {
        setLoader(false);
      }
    } else {
      getLiveCasinoGamesAdapter().then(function (res) {
        if (res && res.length) {
          var casinoGames = res;
          getLiveTables(casinoGames);
          interval = setInterval(getLiveTables, 1000 * 30, casinoGames);
          setTimeout(function () {
            return clearInterval(interval);
          }, 1000 * 60 * 15);
        }
      });
    }

    return function () {
      return clearInterval(interval);
    };
  }, [liveCasinoGames]);
  useEffect(function () {
    setActiveCategory(matchCategory());
  }, [urlParams.category]);
  useEffect(function () {
    console.log('LC ver. - ', version);
  }, []);
  return /*#__PURE__*/React__default.createElement(PageWrapper, null, /*#__PURE__*/React__default.createElement(BannerContainer, null, /*#__PURE__*/React__default.createElement(Banner, {
    key: gamesCount,
    activeCategory: activeCategory,
    gamesCount: gamesCount
  })), /*#__PURE__*/React__default.createElement(MenuContainer, null, /*#__PURE__*/React__default.createElement(CenterBlock, null, /*#__PURE__*/React__default.createElement(ButtonsSwitcher, {
    activeTab: urlParams.category,
    onChange: onChangeTabHandler,
    tabs: LIVE_CASINO_CATEGORIES,
    menuColors: basicMenuColors
  }))), loader ? /*#__PURE__*/React__default.createElement(Loader, null) : /*#__PURE__*/React__default.createElement(AppContext.Provider, {
    value: appContext
  }, /*#__PURE__*/React__default.createElement(Content, {
    activeCategory: activeCategory,
    liveCasinoGames: liveCasino,
    showThumbsByProviders: showThumbsByProviders,
    showGamesByProviders: showGamesByProviders,
    thumbClickHandler: thumbClickHandler,
    gameClickHandler: gameClickHandler,
    gamesCount: function gamesCount(count) {
      setGamesCount(count);
    }
  })));
};

LiveCasino.propTypes = {
  appName: PropTypes.string.isRequired,
  liveCasinoGames: PropTypes.arrayOf(PropTypes.object),
  matchUrl: PropTypes.func.isRequired,
  urlParams: PropTypes.object.isRequired,
  menuColors: PropTypes.object,
  showThumbsByProviders: PropTypes.arrayOf(PropTypes.string),
  showGamesByProviders: PropTypes.arrayOf(PropTypes.string),
  userInfo: PropTypes.object.isRequired,
  thumbClickHandler: PropTypes.func,
  gameClickHandler: PropTypes.func,
  notificationHandler: PropTypes.func
};

export default LiveCasino;
//# sourceMappingURL=index.modern.js.map
